/**
 * Reportes de Tesorería (Sprint 20).
 *
 * Patrón del handoff (design_handoff_tesoreria § TReportes): la página es un
 * CATÁLOGO de reportes — una grilla de tiles (icono + título + descripción +
 * acciones "Ver → · Excel"). "Ver" abre el detalle del reporte en pantalla;
 * "Excel" lo descarga; "PDF"/"Imprimir" abren el diálogo de impresión con el
 * informe en layout ejecutivo (tema claro + logo) — el PDF se obtiene con
 * "Guardar como PDF" del diálogo.
 *
 * Primer corte (SR-07): solo los reportes con backend end-to-end (datos +
 * Excel + detalle) = los 3 nuevos. Los reutilizados (ingresos vs gastos,
 * gastos por categoría, recaudación por casa, conciliación, compensaciones)
 * se suman cuando tengan export + detalle.
 *
 * Patrón Casas: `CatalogoReportesView` es presentacional (reutilizable desde
 * /dev/*); `ReportesPage` maneja la navegación índice↔detalle y el fetch.
 */
import { useCallback, useEffect, useState } from 'react';
import type { ReactNode, SVGProps } from 'react';
import {
  Area,
  AreaChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
} from 'recharts';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Monto, Num } from '../../components/tesoreria/Monto';
import { formatCLP } from '../../lib/format';
import { BarChartIngresosVsGastos } from '../../components/tesoreria/BarChartIngresosVsGastos';
import { DonutChartGastos } from '../../components/tesoreria/DonutChartGastos';
import { InformeMensualVecinos } from '../../components/tesoreria/InformeMensualVecinos';
import { RecordatorioSemestral } from '../../components/tesoreria/RecordatorioSemestral';
import {
  fechaCorta,
  formatearFecha,
  hoy,
  isoAFechaDisplay,
} from '../../lib/tiempo';
import {
  getReporteMorosidad,
  getReporteRecaudacionPorCasa,
  descargarMorosidadXlsx,
  descargarIngresosGastosXlsx,
  descargarGastosCategoriaXlsx,
  descargarConciliacionXlsx,
  descargarCompensacionesXlsx,
  descargarRecaudacionPorCasaXlsx,
  getReporteConciliacion,
  getReporteProveedores,
  descargarProveedoresXlsx,
  getReporteCierreMensual,
  descargarCierreMensualXlsx,
  getReporteResumenAnual,
  descargarResumenAnualXlsx,
  getReporteFlujoCaja,
  descargarFlujoCajaXlsx,
  getReporteFondoReserva,
  descargarFondoReservaXlsx,
  getReporteProyeccion,
  descargarProyeccionXlsx,
  getInformeMensualVecinos,
  getRecordatorioSemestral,
  enviarPruebaRecordatorio,
  enviarRecordatorio,
  enviarInformeMensual,
  enviarPruebaInformeMensual,
  type ReporteMorosidadResponse,
  type PuntoEvolucionMora,
  type ReporteRecaudacionPorCasaResponse,
  type ReporteConciliacionResponse,
  type ReporteProveedoresResponse,
  type ReporteCierreMensualResponse,
  type ReporteResumenAnualResponse,
  type ReporteFlujoCajaResponse,
  type ReporteFondoReservaResponse,
  type ReporteProyeccionResponse,
  type InformeMensualVecinosResponse,
  type RecordatorioSemestralResponse,
  type ItemComposicion,
} from '../../lib/api/reportes-tesoreria';
import {
  getComparativaAnual,
  getGastosPorCategoria,
  type ComparativaAnualResponse,
  type GastosPorCategoriaResponse,
} from '../../lib/api/dashboard-tesoreria';
import { listCompensacionesConDetalle } from '../../lib/api/compensaciones';
import { ApiError } from '../../lib/api/client';
import { useAuth } from '../../lib/auth/AuthContext';
import {
  generarCierreSemestral,
  getCierreSemestral,
  getCierresSemestrales,
  type CierreSemestralDetalle,
  type CierresSemestralesResponse,
  type EstadoCierres,
} from '../../lib/api/cierres-semestrales';
import type { CompensacionConDetalle } from '../../lib/api/types';

// ───────── Iconos (inline, del handoff) ─────────

function IconoAlerta(p: SVGProps<SVGSVGElement>) {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" {...p}>
      <path d="M12 9v4M12 17h.01" />
      <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
    </svg>
  );
}

function IconoTarjeta(p: SVGProps<SVGSVGElement>) {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" {...p}>
      <rect x="2" y="6" width="20" height="13" rx="2" />
      <path d="M2 11h20" />
    </svg>
  );
}

function IconoDoc(p: SVGProps<SVGSVGElement>) {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" {...p}>
      <path d="M14 3H6a2 2 0 00-2 2v14a2 2 0 002 2h12a2 2 0 002-2V9z" />
      <path d="M14 3v6h6M9 13h6M9 17h6" />
    </svg>
  );
}

function IconoEscudo(p: SVGProps<SVGSVGElement>) {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" {...p}>
      <path d="M12 3l8 3v6c0 5-3.5 8.5-8 9-4.5-.5-8-4-8-9V6l8-3z" />
      <path d="M9 12l2 2 4-4" />
    </svg>
  );
}

// ───────── Registro de reportes disponibles ─────────

type ReporteId =
  | 'morosidad'
  | 'ingresos-gastos'
  | 'gastos-categoria'
  | 'compensaciones'
  | 'conciliacion'
  | 'proveedores'
  | 'cierre-mensual'
  | 'informe-vecinos'
  | 'recordatorio-semestral'
  | 'resumen-anual'
  | 'cierre-semestral'
  | 'flujo-caja'
  | 'fondo-reserva'
  | 'proyeccion'
  | 'recaudacion';

interface ReporteMeta {
  id: ReporteId;
  Icono: (p: SVGProps<SVGSVGElement>) => ReactNode;
  color: string;
  titulo: string;
  descripcion: string;
  /** Ausente = el reporte no tiene export XLSX (el informe a los vecinos es
   * un correo, no una planilla) y el botón Excel no se muestra. */
  descargar?: (anio?: number, incluirComp?: boolean) => Promise<void>;
  /** Si el reporte se filtra por año (muestra selector en el detalle). */
  usaAnio: boolean;
  /** Si el reporte ofrece el toggle compensaciones (D-S20-4): solo los de
   * gastos (ingresos vs gastos, gastos por categoría). */
  usaToggle?: boolean;
}

const REPORTES: ReporteMeta[] = [
  {
    id: 'morosidad',
    Icono: IconoAlerta,
    color: '#ff7a7a',
    titulo: 'Estado de morosidad',
    descripcion:
      'Casas con saldo en contra, segmentadas por antigüedad (aging).',
    descargar: descargarMorosidadXlsx,
    usaAnio: false,
  },
  {
    id: 'ingresos-gastos',
    Icono: IconoTarjeta,
    color: '#5fd9b1',
    titulo: 'Ingresos vs gastos',
    descripcion: 'Comparativa mensual de recaudación y gastos del año.',
    descargar: descargarIngresosGastosXlsx,
    usaAnio: true,
    usaToggle: true,
  },
  {
    id: 'gastos-categoria',
    Icono: IconoDoc,
    color: '#b89bff',
    titulo: 'Gastos por categoría',
    descripcion:
      'Distribución de egresos del año por categoría y subcategoría.',
    descargar: descargarGastosCategoriaXlsx,
    usaAnio: true,
    usaToggle: true,
  },
  {
    id: 'recaudacion',
    Icono: IconoDoc,
    color: '#7ab8ff',
    titulo: 'Recaudación por casa',
    descripcion: 'Cuánto transfirió (pagó) cada casa durante el año.',
    descargar: descargarRecaudacionPorCasaXlsx,
    usaAnio: true,
  },
  {
    id: 'compensaciones',
    Icono: IconoDoc,
    color: '#b89bff',
    titulo: 'Estado de compensaciones',
    descripcion: 'Compensaciones por casa: monto, consumido y estado.',
    descargar: descargarCompensacionesXlsx,
    usaAnio: false,
  },
  {
    id: 'conciliacion',
    Icono: IconoEscudo,
    color: '#ffb775',
    titulo: 'Conciliación bancaria',
    descripcion:
      'Cuadre de la cuenta corriente vs. el banco, posición total con depósitos a plazo e histórico de cuadres.',
    descargar: descargarConciliacionXlsx,
    usaAnio: false,
  },
  {
    id: 'proveedores',
    Icono: IconoTarjeta,
    color: '#5fd9b1',
    titulo: 'Análisis de proveedores',
    descripcion:
      'Concentración del gasto por proveedor, dependencia y recurrentes vs. puntuales.',
    descargar: descargarProveedoresXlsx,
    usaAnio: true,
    usaToggle: true,
  },
  {
    id: 'cierre-mensual',
    Icono: IconoDoc,
    color: '#5fd9b1',
    titulo: 'Cierre mensual',
    descripcion:
      'Resumen ejecutivo del mes vs. el anterior: ingresos, gastos, resultado e indicadores.',
    descargar: descargarCierreMensualXlsx,
    usaAnio: false,
    usaToggle: true,
  },
  {
    id: 'informe-vecinos',
    Icono: IconoDoc,
    color: '#ffb775',
    titulo: 'Informe mensual a los vecinos',
    descripcion:
      'El correo de cierre que se envía a los titulares el día 5: resumen del mes, cuánto alcanza el saldo y notas.',
    usaAnio: false,
  },
  {
    id: 'recordatorio-semestral',
    Icono: IconoDoc,
    color: '#ffb775',
    titulo: 'Recordatorio semestral',
    descripcion:
      'El correo de junio y diciembre a las casas con saldo, antes de que empiece la cuota siguiente.',
    usaAnio: false,
  },
  {
    id: 'resumen-anual',
    Icono: IconoDoc,
    color: '#ffb775',
    titulo: 'Resumen anual',
    descripcion:
      'Memoria financiera del ejercicio para la asamblea: ingresos, gastos y composición del año.',
    descargar: descargarResumenAnualXlsx,
    usaAnio: true,
    usaToggle: true,
  },
  {
    id: 'cierre-semestral',
    Icono: IconoDoc,
    color: '#ffb775',
    titulo: 'Cierre semestral',
    descripcion:
      'Foto congelada de cada semestre terminado: ingresos, gastos, saldo y casas con deuda al corte. El 2° semestre trae además el año.',
    usaAnio: false,
  },
  {
    id: 'flujo-caja',
    Icono: IconoTarjeta,
    color: '#6fd3e0',
    titulo: 'Flujo de caja',
    descripcion:
      'Movimiento de efectivo y evolución del saldo de caja mes a mes (base caja).',
    descargar: descargarFlujoCajaXlsx,
    usaAnio: true,
  },
  {
    id: 'fondo-reserva',
    Icono: IconoEscudo,
    color: '#b89bff',
    titulo: 'Fondo de reserva',
    descripcion:
      'Capital en depósitos a plazo frente a la meta (≈6 meses de gastos) y su evolución.',
    descargar: descargarFondoReservaXlsx,
    usaAnio: true,
  },
  {
    id: 'proyeccion',
    Icono: IconoTarjeta,
    color: '#6fd3e0',
    titulo: 'Proyección 12 meses',
    descripcion:
      'Flujo de caja estimado a 12 meses según el promedio de los últimos 12 meses.',
    descargar: descargarProyeccionXlsx,
    usaAnio: false,
  },
];

const MESES_ABREV = [
  'Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun',
  'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic',
];

function formatearTasa(tasa: number | null): string {
  return tasa === null ? '—' : `${(tasa * 100).toFixed(1)} %`;
}

function rgba(hex: string, alpha: number): string {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

// ───────── Catálogo (índice) ─────────

interface CatalogoReportesViewProps {
  onVer: (id: ReporteId) => void;
}

export function CatalogoReportesView({
  onVer,
}: CatalogoReportesViewProps) {
  return (
    <div className="max-w-5xl mx-auto px-4 py-6">
      <div className="mb-5">
        <span className="text-xs uppercase tracking-wide text-orange font-medium">
          Reportes
        </span>
        <h1 className="text-2xl font-semibold text-text-primary mt-1">
          Reportes financieros
        </h1>
        <p className="text-sm text-text-secondary mt-1.5 max-w-xl leading-relaxed">
          Selecciona un reporte para verlo; dentro de cada uno puedes descargarlo
          a Excel o imprimirlo / guardarlo como PDF.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {REPORTES.map((r) => {
          const Icono = r.Icono;
          return (
            <article
              key={r.id}
              className="bg-bg-card border-[0.5px] border-white/[0.08] rounded-md p-[18px] hover:border-orange transition-colors"
            >
              <div className="flex items-start gap-3.5">
                <div
                  className="w-10 h-10 rounded-lg grid place-items-center shrink-0"
                  style={{ background: rgba(r.color, 0.14), color: r.color }}
                >
                  <Icono />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold text-text-primary">
                    {r.titulo}
                  </div>
                  <div className="text-xs text-text-secondary mt-1 leading-relaxed">
                    {r.descripcion}
                  </div>
                  <div className="flex items-center gap-2 mt-3">
                    <button
                      type="button"
                      onClick={() => onVer(r.id)}
                      className="text-xs font-semibold text-orange hover:text-orange-light"
                    >
                      Ver →
                    </button>
                  </div>
                </div>
              </div>
            </article>
          );
        })}
      </div>
    </div>
  );
}

// ───────── Detalle de un reporte ─────────

/** Cabecera ejecutiva de un reporte (RepHeader del handoff): botón volver +
 * eyebrow con fecha + título grande + bajada + controles + botones de export
 * (PDF · Excel · Imprimir; PDF e Imprimir abren el diálogo de impresión).
 * Incluye un encabezado de informe con logo solo visible al imprimir
 * (`.solo-impresion`). Reutilizado por la app real y por el preview /dev. */
export function RepHeader({
  onVolver,
  titulo,
  bajada,
  onExcel,
  controles,
}: {
  onVolver: () => void;
  titulo: string;
  bajada?: ReactNode;
  /** Ausente = el reporte no tiene export XLSX y el botón no se muestra (un
   * botón "Excel" que no descarga nada se lee como app roto). */
  onExcel?: () => void;
  controles?: ReactNode;
}) {
  return (
    <div>
      {/* Encabezado de informe para impresión/PDF (oculto en pantalla). Usa
       * colores claros explícitos a propósito: es un artefacto tinta-sobre-
       * blanco para papel, no la UI de tema oscuro (los tokens son oscuros). */}
      <div className="solo-impresion" style={{ marginBottom: 16 }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            borderBottom: '2px solid #1a1a1a',
            paddingBottom: 10,
          }}
        >
          {/* Escudo completo (naranjo + escudo blanco) en un chip oscuro tipo
           * membrete: el asset está diseñado para fondo oscuro, así se ve
           * entero sobre el blanco del informe. */}
          <div
            style={{
              background: '#1a1a1a',
              borderRadius: 8,
              padding: 6,
              display: 'flex',
              flexShrink: 0,
            }}
          >
            <img
              src="/escudo-calbucura-original.png"
              alt="Escudo Barrio Calbucura"
              style={{ height: 44, width: 'auto', display: 'block' }}
            />
          </div>
          <div>
            <div
              style={{
                fontSize: 11,
                letterSpacing: '0.14em',
                textTransform: 'uppercase',
                color: '#555',
              }}
            >
              Comisión Barrio Calbucura
            </div>
            <div style={{ fontSize: 20, fontWeight: 700, color: '#1a1a1a' }}>
              {titulo}
            </div>
          </div>
          <div
            style={{
              marginLeft: 'auto',
              textAlign: 'right',
              fontSize: 11,
              color: '#555',
            }}
          >
            Emitido el {isoAFechaDisplay(hoy())}
          </div>
        </div>
      </div>

      <button
        type="button"
        onClick={onVolver}
        className="no-imprimir inline-flex items-center gap-1.5 text-xs font-medium text-text-secondary hover:text-text-primary"
      >
        ← Volver a reportes
      </button>
      <div className="flex flex-wrap items-start justify-between gap-3 mt-2">
        <div className="min-w-0">
          <div className="text-xs uppercase tracking-[0.14em] text-orange font-medium">
            Reporte · {isoAFechaDisplay(hoy())}
          </div>
          <h1 className="text-[28px] font-semibold tracking-tight leading-tight text-text-primary mt-1">
            {titulo}
          </h1>
          {bajada && (
            <p className="text-sm text-text-secondary mt-1.5 max-w-xl leading-relaxed">
              {bajada}
            </p>
          )}
        </div>
        <div className="no-imprimir flex flex-wrap items-center gap-2 shrink-0">
          {controles}
          <Button
            variant="secondary"
            size="sm"
            onClick={() => window.print()}
            title="Se guarda como PDF desde el diálogo de impresión"
          >
            PDF
          </Button>
          {onExcel && (
            <Button variant="secondary" size="sm" onClick={onExcel}>
              Excel
            </Button>
          )}
          <Button
            variant="secondary"
            size="sm"
            onClick={() => window.print()}
          >
            Imprimir
          </Button>
        </div>
      </div>
    </div>
  );
}

interface DetalleLayoutProps {
  titulo: string;
  bajada?: ReactNode;
  usaAnio: boolean;
  anioSel: number;
  aniosDisponibles: number[];
  onAnioChange: (anio: number) => void;
  /** Toggle compensaciones (D-S20-4); null = el reporte no lo ofrece. */
  incluirComp: boolean | null;
  onToggleComp: (incluir: boolean) => void;
  onVolver: () => void;
  onExcel?: () => void;
  loading: boolean;
  error: string | null;
  children: ReactNode;
}

function DetalleLayout({
  titulo,
  bajada,
  usaAnio,
  anioSel,
  aniosDisponibles,
  onAnioChange,
  incluirComp,
  onToggleComp,
  onVolver,
  onExcel,
  loading,
  error,
  children,
}: DetalleLayoutProps) {
  const controles = (
    <>
      {usaAnio && (
        <label className="flex items-center gap-2 text-sm text-text-secondary">
          Año
          <select
            value={anioSel}
            onChange={(e) => onAnioChange(Number(e.target.value))}
            className="bg-bg-card border-[0.5px] border-white/15 rounded-sm px-2 py-1 text-text-primary"
          >
            {aniosDisponibles.map((a) => (
              <option key={a} value={a}>
                {a}
              </option>
            ))}
          </select>
        </label>
      )}
      {incluirComp !== null && (
        <label className="flex items-center gap-2 text-sm text-text-secondary">
          <input
            type="checkbox"
            checked={incluirComp}
            onChange={(e) => onToggleComp(e.target.checked)}
          />
          Incluir compensaciones
        </label>
      )}
    </>
  );
  return (
    <div className="area-imprimible max-w-5xl mx-auto px-4 py-6 space-y-4">
      <RepHeader
        onVolver={onVolver}
        titulo={titulo}
        bajada={bajada}
        onExcel={onExcel}
        controles={controles}
      />
      {error && (
        <div className="bg-state-danger/10 border-[0.5px] border-state-danger/30 text-state-danger rounded-md p-3 text-sm">
          {error}
        </div>
      )}
      {loading && (
        <div className="text-text-secondary text-sm">Cargando reporte…</div>
      )}
      {!loading && !error && children}
    </div>
  );
}

// ───────── Primitivas de reportería (handoff: RepBand / RepNote / RepLine) ─────────

interface KpiItem {
  label: string;
  value: ReactNode;
  sub?: ReactNode;
  trendPct?: number;
  trendBad?: boolean;
  big?: boolean;
  /** Color sólido del valor (ej. rojo para morosidad). Default: text-primary. */
  valueColor?: string;
}

/** Banda de KPIs ejecutivos (RepBand del handoff): 4 celdas, valor grande,
 * trend opcional con color semántico. Responsive: 4→2→1 columnas. */
function RepBand({ items, accent }: { items: KpiItem[]; accent?: string }) {
  return (
    <div
      className="rounded-md border-[0.5px] overflow-hidden"
      style={{ borderColor: accent ?? 'rgba(255,255,255,0.08)' }}
    >
      <div className="grid grid-cols-2 md:grid-cols-4 divide-x divide-y md:divide-y-0 divide-white/[0.06] bg-bg-sunken">
        {items.map((k, i) => (
          <div key={i} className="p-4">
            <div className="text-xs text-text-secondary">{k.label}</div>
            <div className="flex items-baseline gap-2 mt-1">
              <span
                className={`font-medium tracking-tight ${
                  k.big ? 'text-3xl' : 'text-2xl'
                } ${k.valueColor ? '' : 'text-text-primary'}`}
                style={k.valueColor ? { color: k.valueColor } : undefined}
              >
                {k.value}
              </span>
              {k.trendPct != null && (
                <span
                  className="text-xs font-semibold"
                  style={{
                    // Direccional: para una métrica donde subir es malo
                    // (trendBad), ↑ es rojo y ↓ verde; si subir es bueno, al
                    // revés. Coherente con el pill de la tarjeta de evolución.
                    color: (
                      k.trendBad ? k.trendPct >= 0 : k.trendPct < 0
                    )
                      ? '#ff7a7a'
                      : '#5fd9b1',
                    opacity: 0.8,
                  }}
                >
                  {k.trendPct >= 0 ? '↑' : '↓'}
                  <Num>{Math.abs(k.trendPct).toFixed(1)}</Num>%
                </span>
              )}
            </div>
            {k.sub && (
              <div className="text-xs text-text-secondary mt-1">{k.sub}</div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

/** Nota metodológica al pie (RepNote del handoff). */
function RepNote({ children }: { children: ReactNode }) {
  return (
    <div className="bg-bg-card border-[0.5px] border-white/[0.08] rounded-md p-3 text-xs text-text-secondary leading-relaxed">
      {children}
    </div>
  );
}

const MESES_MIN = [
  'Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun',
  'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic',
];

/** Mini line+area de evolución (RepLine del handoff). */
function EvolucionMoraChart({ data }: { data: PuntoEvolucionMora[] }) {
  const rows = data.map((p) => ({ mes: MESES_MIN[p.mes - 1], monto: p.monto }));
  return (
    <div className="w-full" style={{ height: 140 }}>
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart data={rows} margin={{ top: 8, right: 8, left: 8, bottom: 0 }}>
          <defs>
            <linearGradient id="gradMora" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#ff7a7a" stopOpacity={0.3} />
              <stop offset="100%" stopColor="#ff7a7a" stopOpacity={0} />
            </linearGradient>
          </defs>
          <XAxis
            dataKey="mes"
            tick={{ fill: '#8a8479', fontSize: 10 }}
            axisLine={false}
            tickLine={false}
          />
          <Tooltip
            contentStyle={{
              backgroundColor: '#1a1a1d',
              border: '1px solid #26262a',
              borderRadius: 6,
              fontSize: 12,
              color: '#f4ede0',
            }}
            formatter={(v) => formatCLP(Number(v))}
          />
          <Area
            type="monotone"
            dataKey="monto"
            stroke="#ff7a7a"
            strokeWidth={2}
            fill="url(#gradMora)"
            dot={{ r: 2, fill: '#ff7a7a', strokeWidth: 0 }}
            activeDot={{ r: 4 }}
          />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
}

function accionMora(meses: number): string {
  if (meses >= 4) return 'Contactar a vecino!';
  if (meses >= 2) return 'Notificar y agendar pago';
  return 'Recordatorio de pago';
}

function colorSeveridad(meses: number): string {
  if (meses >= 4) return '#ff7a7a';
  if (meses >= 2) return '#ff9d5c';
  return '#ffb775';
}

export function MorosidadDetalle({
  reporte,
}: {
  reporte: ReporteMorosidadResponse;
}) {
  const pctBarrio = reporte.total_casas
    ? (reporte.casas_morosas / reporte.total_casas) * 100
    : 0;
  // Trend vs mes anterior desde la serie de evolución (último vs penúltimo).
  const ev = reporte.evolucion;
  const prev = ev.length >= 2 ? ev[ev.length - 2].monto : 0;
  const ultimo = ev.length >= 1 ? ev[ev.length - 1].monto : 0;
  const trendMora =
    prev > 0 ? ((ultimo - prev) / prev) * 100 : undefined;
  const mesPrevNombre =
    ev.length >= 2 ? MESES_MIN[ev[ev.length - 2].mes - 1] : '';
  return (
    <div className="space-y-4">
      <RepBand
        accent="rgba(255,122,122,0.28)"
        items={[
          {
            label: 'Morosidad total',
            value: <Monto value={reporte.total_mora} />,
            big: true,
            valueColor: '#ff7a7a',
            trendPct: trendMora,
            trendBad: true,
          },
          {
            label: 'Casas morosas',
            value: <Num>{reporte.casas_morosas}</Num>,
            sub: `de ${reporte.total_casas} · ${pctBarrio.toFixed(0)}% del barrio`,
          },
          {
            label: '% cartera semestral',
            value: (
              <Num>
                {reporte.pct_cartera === null
                  ? '—'
                  : `${(reporte.pct_cartera * 100).toFixed(1)} %`}
              </Num>
            ),
            sub: (
              <>
                vs <Monto value={reporte.cartera_semestral} /> del semestre
              </>
            ),
          },
          {
            label: 'Deuda promedio',
            value: <Monto value={reporte.deuda_promedio} />,
            sub: 'por casa morosa',
          },
        ]}
      />

      <div className="grid md:grid-cols-[1.5fr_1fr] gap-4">
        <Card title="Antigüedad de la deuda">
          <div className="text-xs text-text-secondary -mt-2 mb-3">
            Segmentación por meses de atraso (aging)
          </div>
          {reporte.total_mora > 0 && (
            <div className="flex h-3 rounded-full overflow-hidden mb-4 bg-bg-sunken">
              {reporte.buckets
                .filter((b) => b.monto > 0)
                .map((b) => (
                  <div
                    key={b.id}
                    title={`${b.label}: ${formatCLP(b.monto)}`}
                    style={{
                      width: `${(b.monto / reporte.total_mora) * 100}%`,
                      background: b.color,
                    }}
                  />
                ))}
            </div>
          )}
          <div className="space-y-2.5">
            {reporte.buckets.map((b) => (
              <div
                key={b.id}
                className="flex items-center justify-between gap-3"
              >
                <div className="flex items-center gap-2.5 min-w-0">
                  <span
                    className="w-2.5 h-2.5 rounded-sm shrink-0"
                    style={{ background: b.color }}
                  />
                  <div className="min-w-0">
                    <div className="text-sm">{b.label}</div>
                    <div className="text-xs text-text-secondary">
                      {b.descripcion}
                    </div>
                  </div>
                </div>
                <div className="text-right shrink-0">
                  <div className="text-sm font-medium">
                    <Monto value={b.monto} />
                  </div>
                  <div className="text-xs text-text-secondary">
                    <Num>{b.casas}</Num> casa{b.casas !== 1 ? 's' : ''} ·{' '}
                    {reporte.total_mora
                      ? ((b.monto / reporte.total_mora) * 100).toFixed(0)
                      : 0}
                    %
                  </div>
                </div>
              </div>
            ))}
          </div>
        </Card>

        <Card title="Evolución morosidad">
          <div className="text-xs text-text-secondary -mt-2 mb-2">
            Últimos 6 meses
          </div>
          {trendMora !== undefined && (
            <span
              className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold mb-3"
              style={{
                background:
                  trendMora >= 0
                    ? 'rgba(255,122,122,0.12)'
                    : 'rgba(95,217,177,0.12)',
                color: trendMora >= 0 ? '#ff7a7a' : '#5fd9b1',
              }}
            >
              {trendMora >= 0 ? '↑' : '↓'}{' '}
              <Num>{Math.abs(trendMora).toFixed(1)}</Num>% vs {mesPrevNombre}
            </span>
          )}
          <EvolucionMoraChart data={reporte.evolucion} />
        </Card>
      </div>

      <Card title="Detalle por casa">
        {reporte.casas.length === 0 ? (
          <p className="text-sm text-text-secondary">
            No hay casas con deuda. 🎉
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-text-secondary text-left border-b border-white/[0.08]">
                  <th className="py-1 pr-3 font-medium">Casa</th>
                  <th className="py-1 pr-3 font-medium">Titular</th>
                  <th className="py-1 pr-3 font-medium text-center">Mora</th>
                  <th className="py-1 pr-3 font-medium">Último pago</th>
                  <th className="py-1 pr-3 font-medium text-right">Deuda</th>
                  <th className="py-1 font-medium">Acción recomendada</th>
                </tr>
              </thead>
              <tbody>
                {reporte.casas.map((c) => (
                  <tr key={c.lote_id} className="border-b border-white/[0.04]">
                    <td className="py-1.5 pr-3 font-medium">{c.identificador}</td>
                    <td className="py-1.5 pr-3">{c.titular_nombre ?? '—'}</td>
                    <td className="py-1.5 pr-3 text-center">
                      <span
                        className="font-semibold"
                        style={{ color: colorSeveridad(c.meses_mora) }}
                      >
                        <Num>{c.meses_mora}</Num>m
                      </span>
                    </td>
                    <td className="py-1.5 pr-3 text-text-secondary">
                      {c.ultimo_pago ? formatearFecha(c.ultimo_pago) : '—'}
                    </td>
                    <td className="py-1.5 pr-3 text-right text-state-danger">
                      <Monto value={c.saldo_a_hoy} />
                    </td>
                    <td className="py-1.5 text-text-secondary">
                      {accionMora(c.meses_mora)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="flex justify-between items-center pt-3 mt-1 border-t border-white/[0.08]">
              <span className="text-sm font-medium text-text-secondary">
                Total morosidad
              </span>
              <span className="text-base font-semibold text-state-danger">
                <Monto value={reporte.total_mora} />
              </span>
            </div>
          </div>
        )}
      </Card>

      <RepNote>
        <strong className="text-text-primary">Metodología:</strong> la morosidad
        considera cuotas vencidas no pagadas al corte de hoy. Las casas cubiertas
        por saldo a favor se excluyen. La antigüedad (meses de mora) se calcula
        con el régimen lineal de la cuota.
      </RepNote>
    </div>
  );
}

/** Monto con signo +/− y color verde/rojo (para neto/resultado). */
function MontoFirmado({ value, neutro }: { value: number; neutro?: boolean }) {
  const color = neutro
    ? undefined
    : value >= 0
      ? '#5fd9b1'
      : '#ff7a7a';
  return (
    <span style={color ? { color } : undefined}>
      {value >= 0 ? '+' : '−'}
      <Monto value={Math.abs(value)} />
    </span>
  );
}

export function IngresosGastosDetalle({
  reporte,
}: {
  reporte: ComparativaAnualResponse;
}) {
  const totIng = reporte.meses.reduce((s, m) => s + m.recaudacion, 0);
  const totGas = reporte.meses.reduce((s, m) => s + m.gastos, 0);
  const neto = totIng - totGas;
  const margen = totIng > 0 ? (neto / totIng) * 100 : null;
  let acc = 0;
  const filas = reporte.meses.map((m) => {
    const n = m.recaudacion - m.gastos;
    acc += n;
    return { m, neto: n, acc };
  });
  return (
    <div className="space-y-4">
      <RepBand
        accent="rgba(95,217,177,0.28)"
        items={[
          {
            label: 'Ingresos del año',
            value: <Monto value={totIng} />,
            valueColor: '#5fd9b1',
          },
          {
            label: 'Gastos del año',
            value: <Monto value={totGas} />,
            valueColor: '#ff7a7a',
          },
          {
            label: 'Resultado neto',
            value: <MontoFirmado value={neto} />,
            big: true,
            valueColor: neto >= 0 ? '#5fd9b1' : '#ff7a7a',
          },
          {
            label: 'Margen',
            value: (
              <Num>{margen === null ? '—' : `${margen.toFixed(1)} %`}</Num>
            ),
            sub: 'del ingreso total',
          },
        ]}
      />

      <Card title="Evolución mensual">
        <BarChartIngresosVsGastos meses={reporte.meses} />
      </Card>

      <Card title="Detalle mensual">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-text-secondary text-left border-b border-white/[0.08]">
                <th className="py-1 pr-3 font-medium">Mes</th>
                <th className="py-1 pr-3 font-medium text-right">Ingresos</th>
                <th className="py-1 pr-3 font-medium text-right">Gastos</th>
                <th className="py-1 pr-3 font-medium text-right">Neto</th>
                <th className="py-1 font-medium text-right">Acumulado</th>
              </tr>
            </thead>
            <tbody>
              {filas.map(({ m, neto: n, acc: a }) => (
                <tr
                  key={m.mes}
                  className={`border-b border-white/[0.04] ${
                    m.es_futuro ? 'opacity-40' : ''
                  }`}
                >
                  <td className="py-1 pr-3">
                    {MESES_ABREV[Number(m.mes.slice(5, 7)) - 1]}
                  </td>
                  <td className="py-1 pr-3 text-right" style={{ color: '#5fd9b1' }}>
                    <Monto value={m.recaudacion} />
                  </td>
                  <td className="py-1 pr-3 text-right" style={{ color: '#ff7a7a' }}>
                    <Monto value={m.gastos} />
                  </td>
                  <td className="py-1 pr-3 text-right">
                    <MontoFirmado value={n} />
                  </td>
                  <td className="py-1 text-right">
                    <MontoFirmado value={a} neutro />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="flex justify-between items-center pt-3 mt-1 border-t border-white/[0.08] text-sm font-medium">
            <span className="text-text-secondary">Resultado del año</span>
            <MontoFirmado value={neto} />
          </div>
        </div>
      </Card>

      <RepNote>
        El resultado neto considera ingresos percibidos y gastos pagados en cada
        mes. El acumulado parte de cero en enero. Las compensaciones se incluyen
        o excluyen según el toggle del encabezado (efecto neto cero en banco).
      </RepNote>
    </div>
  );
}

export function GastosCategoriaDetalle({
  reporte,
}: {
  reporte: GastosPorCategoriaResponse;
}) {
  const items = [...reporte.items].sort((a, b) => b.monto - a.monto);
  const total = reporte.total;
  const [expandidas, setExpandidas] = useState<Set<string>>(
    () => new Set(items[0] ? [items[0].categoria_id] : []),
  );

  if (items.length === 0) {
    return (
      <Card>
        <p className="text-sm text-text-secondary">Sin gastos en el período.</p>
      </Card>
    );
  }

  const nSubs = items.reduce((s, c) => s + c.hijos.length, 0);
  const mayor = items[0];
  const todasAbiertas = expandidas.size === items.length;
  const toggle = (id: string) =>
    setExpandidas((prev) => {
      const n = new Set(prev);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });

  return (
    <div className="space-y-4">
      <RepBand
        accent="rgba(184,155,255,0.28)"
        items={[
          { label: 'Gasto total', value: <Monto value={total} />, big: true },
          {
            label: 'Categorías',
            value: <Num>{items.length}</Num>,
            sub: `${nSubs} subcategorías`,
          },
          {
            label: 'Mayor categoría',
            value: mayor.categoria_nombre,
            sub: `${total ? ((mayor.monto / total) * 100).toFixed(0) : 0}% · ${formatCLP(mayor.monto)}`,
          },
          {
            label: 'Promedio / categoría',
            value: <Monto value={Math.round(total / items.length)} />,
          },
        ]}
      />

      <Card title="Distribución de gastos">
        <DonutChartGastos items={reporte.items} total={total} />
      </Card>

      <Card
        title="Desglose jerárquico"
        actionRight={
          <button
            type="button"
            onClick={() =>
              setExpandidas(
                todasAbiertas
                  ? new Set()
                  : new Set(items.map((i) => i.categoria_id)),
              )
            }
            className="text-xs font-semibold text-orange hover:text-orange-light"
          >
            {todasAbiertas ? 'Colapsar todo' : 'Expandir todo'}
          </button>
        }
      >
        <div>
          {items.map((c, i) => {
            const abierta = expandidas.has(c.categoria_id);
            const pct = total ? (c.monto / total) * 100 : 0;
            return (
              <div
                key={c.categoria_id}
                className={i < items.length - 1 ? 'border-b border-white/[0.04]' : ''}
              >
                <button
                  type="button"
                  onClick={() => toggle(c.categoria_id)}
                  className="w-full grid grid-cols-[16px_1fr_auto_44px] gap-3 items-center py-3 text-left"
                >
                  <span
                    className="text-text-secondary transition-transform"
                    style={{ transform: abierta ? 'none' : 'rotate(-90deg)' }}
                  >
                    ▾
                  </span>
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <span
                        className="w-2.5 h-2.5 rounded-sm shrink-0"
                        style={{ background: c.categoria_color }}
                      />
                      <span className="text-sm font-semibold text-text-primary truncate">
                        {c.categoria_nombre}
                      </span>
                      <span className="text-xs text-text-muted">
                        {c.hijos.length} ítems
                      </span>
                    </div>
                    <div className="h-1.5 bg-bg-sunken rounded-full overflow-hidden mt-2">
                      <div
                        className="h-full rounded-full"
                        style={{ width: `${pct}%`, background: c.categoria_color }}
                      />
                    </div>
                  </div>
                  <div className="text-sm font-semibold text-right">
                    <Monto value={c.monto} />
                  </div>
                  <div className="text-xs text-text-secondary text-right">
                    {pct.toFixed(0)}%
                  </div>
                </button>
                {abierta && (
                  <div className="pb-2.5">
                    {[...c.hijos]
                      .sort((a, b) => b.monto - a.monto)
                      .map((h) => (
                        <div
                          key={h.categoria_id}
                          className="grid grid-cols-[16px_1fr_auto_44px] gap-3 items-center py-1.5"
                        >
                          <span />
                          <div className="flex items-center gap-2 pl-5">
                            <span
                              className="w-1.5 h-1.5 rounded-full shrink-0 opacity-60"
                              style={{ background: c.categoria_color }}
                            />
                            <span className="text-[13px] text-text-secondary">
                              {h.categoria_nombre}
                            </span>
                          </div>
                          <div className="text-[13px] text-text-secondary text-right">
                            <Monto value={h.monto} />
                          </div>
                          <div className="text-xs text-text-muted text-right">
                            {c.monto ? ((h.monto / c.monto) * 100).toFixed(0) : 0}%
                          </div>
                        </div>
                      ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
        <div className="flex justify-between items-center pt-3 mt-1 border-t border-white/[0.08] text-sm font-medium">
          <span className="text-text-secondary">Total gastos del período</span>
          <span className="text-text-primary">
            <Monto value={total} />
          </span>
        </div>
      </Card>

      <RepNote>
        Las subcategorías corresponden a la tabla de clasificación configurada.
        El desglose permite ver dentro de cada rubro padre dónde se concentra el
        gasto. Excluye fondos de terceros.
      </RepNote>
    </div>
  );
}

export function CompensacionesDetalle({
  comps,
}: {
  comps: CompensacionConDetalle[];
}) {
  if (comps.length === 0) {
    return (
      <Card>
        <p className="text-sm text-text-secondary">No hay compensaciones.</p>
      </Card>
    );
  }
  const totalComp = comps.reduce((s, c) => s + c.monto, 0);
  const consumido = comps.reduce((s, c) => s + c.consumido, 0);
  const restante = totalComp - consumido;
  const activas = comps.filter((c) => c.activa).length;
  return (
    <div className="space-y-4">
      <RepBand
        accent="rgba(184,155,255,0.28)"
        items={[
          {
            label: 'Total comprometido',
            value: <Monto value={totalComp} />,
            big: true,
            valueColor: '#b89bff',
          },
          {
            label: 'Consumido',
            value: <Monto value={consumido} />,
            valueColor: '#5fd9b1',
          },
          {
            label: 'Por consumir',
            value: <Monto value={restante} />,
            valueColor: '#ffb775',
          },
          {
            label: 'Activas',
            value: <Num>{activas}</Num>,
            sub: `de ${comps.length} acuerdos`,
          },
        ]}
      />
      <Card title="Detalle de compensaciones">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-text-secondary text-left border-b border-white/[0.08]">
                <th className="py-1 pr-3 font-medium">Casa</th>
                <th className="py-1 pr-3 font-medium">Vecino</th>
                <th className="py-1 pr-3 font-medium">Categoría</th>
                <th className="py-1 pr-3 font-medium text-right">Total</th>
                <th className="py-1 pr-3 font-medium text-right">Consumido</th>
                <th className="py-1 pr-3 font-medium text-right">Restante</th>
                <th className="py-1 font-medium">Estado</th>
              </tr>
            </thead>
            <tbody>
              {comps.map((c) => {
                const rest = c.monto - c.consumido;
                return (
                  <tr key={c.id} className="border-b border-white/[0.04]">
                    <td className="py-1.5 pr-3 font-medium">
                      {c.lote_identificador}
                    </td>
                    <td className="py-1.5 pr-3">{c.vecino_nombre}</td>
                    <td className="py-1.5 pr-3">{c.categoria_nombre}</td>
                    <td className="py-1.5 pr-3 text-right">
                      <Monto value={c.monto} />
                    </td>
                    <td
                      className="py-1.5 pr-3 text-right"
                      style={{ color: '#5fd9b1' }}
                    >
                      <Monto value={c.consumido} />
                    </td>
                    <td
                      className="py-1.5 pr-3 text-right"
                      style={{ color: rest === 0 ? undefined : '#ffb775' }}
                    >
                      <Monto value={rest} />
                    </td>
                    <td className="py-1.5">
                      <span
                        className="text-[10.5px] font-semibold px-2 py-0.5 rounded-full"
                        style={{
                          background: c.activa
                            ? 'rgba(255,183,117,0.14)'
                            : 'rgba(95,217,177,0.14)',
                          color: c.activa ? '#ffb775' : '#5fd9b1',
                        }}
                      >
                        {c.activa ? 'Activa' : 'Cerrada'}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>
      <RepNote>
        Cada compensación genera un gasto y un ingreso por el mismo monto
        (efecto neto en banco cero). El consumo se aplica mensualmente contra
        cuotas o accesorios.
      </RepNote>
    </div>
  );
}

/** Tasa en puntos base → texto "X,XX %" (100 bps = 1%). */
function formatearBps(bps: number | null): string {
  return bps === null ? '—' : `${(bps / 100).toFixed(2)} %`;
}

function montoONulo(v: number | null): ReactNode {
  return v === null ? '—' : <Monto value={v} />;
}

export function ConciliacionDetalle({
  reporte,
}: {
  reporte: ReporteConciliacionResponse;
}) {
  const r = reporte;
  const colorDif =
    r.diferencia === null
      ? undefined
      : r.diferencia === 0
        ? '#5fd9b1'
        : '#ff7a7a';
  const mov = r.movimientos_desde_cuadre;
  return (
    <div className="space-y-4">
      {!r.tiene_cuadre && (
        <div className="bg-bg-card border-[0.5px] border-orange/30 text-text-secondary rounded-md p-3 text-sm">
          Aún no hay cuadres bancarios registrados. Registra el saldo de la
          cuenta en <strong className="text-text-primary">Banco</strong> para ver
          la conciliación y la posición total.
        </div>
      )}

      <RepBand
        accent="rgba(255,183,117,0.28)"
        items={[
          {
            label: 'Saldo banco',
            value: montoONulo(r.saldo_banco),
            big: true,
            sub: 'corriente · último cuadre',
          },
          {
            label: 'Saldo calculado',
            value: montoONulo(r.saldo_calculado),
            sub: 'según el sistema',
          },
          {
            label: 'Diferencia',
            value: montoONulo(r.diferencia),
            valueColor: colorDif,
            sub: r.diferencia === 0 ? 'cuadra ✓' : 'banco − sistema',
          },
          {
            label: 'Posición total',
            value: montoONulo(r.posicion_total),
            valueColor: '#ffb775',
            sub: 'corriente + depósitos a plazo',
          },
        ]}
      />

      <div className="grid md:grid-cols-2 gap-4">
        <Card title="Estimación a hoy">
          <div className="text-xs text-text-secondary -mt-2 mb-3">
            {r.ultimo_cuadre_fecha
              ? `Desde el último cuadre (${formatearFecha(r.ultimo_cuadre_fecha)})`
              : 'Sin cuadre de referencia'}
          </div>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-text-secondary">+ Ingresos (transferencias)</span>
              <span style={{ color: '#5fd9b1' }}>
                <Monto value={mov.ingresos} />
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-text-secondary">− Gastos</span>
              <span style={{ color: '#ff7a7a' }}>
                <Monto value={mov.gastos} />
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-text-secondary">
                {mov.traspasos_dap >= 0 ? '− Traspaso neto a DAP' : '+ Liquidación neta DAP'}
              </span>
              <span>
                <Monto value={Math.abs(mov.traspasos_dap)} />
              </span>
            </div>
            <div className="flex justify-between pt-2 mt-1 border-t border-white/[0.08]">
              <span className="font-medium">Saldo calculado a hoy</span>
              <span className="font-semibold">
                {montoONulo(r.saldo_calculado_hoy)}
              </span>
            </div>
          </div>
        </Card>

        <Card title="Depósitos a plazo vigentes">
          {r.daps_vigentes.length === 0 ? (
            <p className="text-sm text-text-secondary">
              Sin depósitos a plazo vigentes.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-text-secondary text-left border-b border-white/[0.08]">
                    <th className="py-1 pr-3 font-medium">Banco</th>
                    <th className="py-1 pr-3 font-medium">Vence</th>
                    <th className="py-1 pr-3 font-medium text-right">Tasa</th>
                    <th className="py-1 font-medium text-right">Capital</th>
                  </tr>
                </thead>
                <tbody>
                  {r.daps_vigentes.map((d) => (
                    <tr key={d.id} className="border-b border-white/[0.04]">
                      <td className="py-1.5 pr-3">{d.banco}</td>
                      <td className="py-1.5 pr-3 text-text-secondary">
                        {formatearFecha(d.fecha_vencimiento)}
                      </td>
                      <td className="py-1.5 pr-3 text-right text-text-secondary">
                        {formatearBps(d.tasa_bps)}
                      </td>
                      <td className="py-1.5 text-right">
                        <Monto value={d.capital} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="flex justify-between items-center pt-3 mt-1 border-t border-white/[0.08]">
                <span className="text-sm font-medium text-text-secondary">
                  Capital inmovilizado
                </span>
                <span className="text-base font-semibold" style={{ color: '#ffb775' }}>
                  <Monto value={r.capital_dap_vigente} />
                </span>
              </div>
            </div>
          )}
        </Card>
      </div>

      <Card title="Histórico de cuadres">
        {r.historico.length === 0 ? (
          <p className="text-sm text-text-secondary">
            Aún no hay cuadres registrados.
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-text-secondary text-left border-b border-white/[0.08]">
                  <th className="py-1 pr-3 font-medium">Fecha</th>
                  <th className="py-1 pr-3 font-medium text-right">Saldo banco</th>
                  <th className="py-1 pr-3 font-medium text-right">Saldo calculado</th>
                  <th className="py-1 pr-3 font-medium text-right">Diferencia</th>
                  <th className="py-1 font-medium">Notas</th>
                </tr>
              </thead>
              <tbody>
                {r.historico.map((h, i) => (
                  <tr key={i} className="border-b border-white/[0.04]">
                    <td className="py-1.5 pr-3">
                      {formatearFecha(h.fecha_registro)}
                    </td>
                    <td className="py-1.5 pr-3 text-right">
                      <Monto value={h.saldo_banco} />
                    </td>
                    <td className="py-1.5 pr-3 text-right text-text-secondary">
                      <Monto value={h.saldo_calculado} />
                    </td>
                    <td
                      className={`py-1.5 pr-3 text-right ${
                        h.diferencia ? 'text-state-danger' : 'text-text-secondary'
                      }`}
                    >
                      <Monto value={h.diferencia} />
                    </td>
                    <td className="py-1.5 text-text-secondary">
                      {h.notas ?? '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <RepNote>
        <strong className="text-text-primary">Metodología:</strong> el barrio opera
        una sola cuenta corriente y solo por transferencias. El{' '}
        <strong className="text-text-primary">saldo banco</strong> y la{' '}
        <strong className="text-text-primary">diferencia</strong> son los del
        último cuadre registrado; el saldo calculado a hoy proyecta los
        movimientos desde entonces. El capital de los depósitos a plazo sale de
        la corriente (traspaso, no gasto) y vuelve al liquidar, por eso se suma
        aparte en la <strong className="text-text-primary">posición total</strong>.
      </RepNote>
    </div>
  );
}

const ESTADO_CASA: Record<string, { label: string; color: string }> = {
  al_dia: { label: 'Al día', color: '#5fd9b1' },
  al_dia_pendiente_semestre: { label: 'Al día (pend.)', color: '#ffb775' },
  atrasada: { label: 'Atrasada', color: '#ff7a7a' },
  suspendida: { label: 'Suspendida', color: '#ff7a7a' },
  compensacion: { label: 'Compensación', color: '#b89bff' },
};

function EstadoBadge({ estado }: { estado: string }) {
  const e = ESTADO_CASA[estado] ?? { label: estado, color: '#8a8479' };
  return (
    <span
      className="text-[10.5px] font-semibold px-2 py-0.5 rounded-full"
      style={{ background: `${e.color}22`, color: e.color }}
    >
      {e.label}
    </span>
  );
}

export function RecaudacionDetalle({
  reporte,
}: {
  reporte: ReporteRecaudacionPorCasaResponse;
}) {
  const cumpleOk =
    reporte.cumplimiento !== null && reporte.cumplimiento >= 0.85;
  return (
    <div className="space-y-4">
      <RepBand
        accent="rgba(122,184,255,0.28)"
        items={[
          {
            label: 'Recaudado del barrio',
            value: <Monto value={reporte.total_recaudado_barrio} />,
            big: true,
            valueColor: '#7ab8ff',
          },
          {
            label: 'Esperado (a hoy)',
            value: <Monto value={reporte.esperado} />,
            sub: `${reporte.total_casas} casas × ${reporte.meses_transcurridos} meses`,
          },
          {
            label: 'Cumplimiento',
            value: (
              <Num>
                {reporte.cumplimiento === null
                  ? '—'
                  : `${(reporte.cumplimiento * 100).toFixed(1)} %`}
              </Num>
            ),
            valueColor: cumpleOk ? '#5fd9b1' : '#ffb775',
          },
          {
            label: 'Casas al día',
            value: <Num>{reporte.casas_al_dia}</Num>,
            sub: `de ${reporte.total_casas}`,
          },
        ]}
      />

      <Card title="Detalle por casa">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-text-secondary text-left border-b border-white/[0.08]">
                <th className="py-1 pr-3 font-medium">Casa</th>
                <th className="py-1 pr-3 font-medium">Titular</th>
                <th className="py-1 pr-3 font-medium text-center">
                  Cuotas pagadas
                </th>
                <th className="py-1 pr-3 font-medium">Estado</th>
                <th className="py-1 pr-3 font-medium text-right">Barrio</th>
                <th className="py-1 font-medium text-right">A terceros</th>
              </tr>
            </thead>
            <tbody>
              {reporte.casas.map((c) => (
                <tr key={c.lote_id} className="border-b border-white/[0.04]">
                  <td className="py-1.5 pr-3 font-medium">{c.identificador}</td>
                  <td className="py-1.5 pr-3">{c.titular_nombre ?? '—'}</td>
                  <td className="py-1.5 pr-3 text-center">
                    <Num>
                      {c.cuotas_pagadas}/{reporte.meses_transcurridos}
                    </Num>
                  </td>
                  <td className="py-1.5 pr-3">
                    <EstadoBadge estado={c.estado} />
                  </td>
                  <td
                    className={`py-1.5 pr-3 text-right ${
                      c.recaudado_barrio === 0 ? 'text-text-secondary' : ''
                    }`}
                  >
                    <Monto value={c.recaudado_barrio} />
                  </td>
                  <td className="py-1.5 text-right text-text-secondary">
                    {c.aportado_terceros > 0 ? (
                      <Monto value={c.aportado_terceros} />
                    ) : (
                      '—'
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="flex justify-between items-center pt-3 mt-1 border-t border-white/[0.08]">
            <span className="text-sm font-medium text-text-secondary">
              Total recaudado del barrio
            </span>
            <span className="text-base font-semibold" style={{ color: '#7ab8ff' }}>
              <Monto value={reporte.total_recaudado_barrio} />
            </span>
          </div>
        </div>
      </Card>

      <RepNote>
        El aporte se calcula sobre las cuotas pagadas o compensadas; no incluye
        ingresos varios (multas, accesorios). La columna{' '}
        <strong className="text-text-primary">A terceros</strong> es dinero que
        la casa transfirió a campañas externas (muni/JJVV): tocó el banco pero{' '}
        <strong className="text-text-primary">no</strong> es ingreso del barrio
        {reporte.total_terceros > 0 && (
          <>
            {' '}(total <Monto value={reporte.total_terceros} />)
          </>
        )}
        .
      </RepNote>
    </div>
  );
}

export function ProveedoresDetalle({
  reporte,
}: {
  reporte: ReporteProveedoresResponse;
}) {
  const r = reporte;
  const principal = r.proveedores[0];
  const topN = r.proveedores.slice(0, 8);
  const total = r.total_gastado || 1; // evita /0 en los anchos
  return (
    <div className="space-y-4">
      <RepBand
        accent="rgba(95,217,177,0.28)"
        items={[
          {
            label: 'Total gastado',
            value: <Monto value={r.total_gastado} />,
            big: true,
            valueColor: '#5fd9b1',
          },
          {
            label: 'Proveedores',
            value: <Num>{r.n_proveedores}</Num>,
            sub: `${r.recurrentes.n} recurrentes · ${r.puntuales.n} puntuales`,
          },
          {
            label: 'Concentración top-3',
            value: <Num>{formatearTasa(r.concentracion_top3)}</Num>,
            sub: 'del gasto en los 3 mayores',
            valueColor: '#ffb775',
          },
          {
            label: 'Ticket promedio',
            value: <Monto value={r.ticket_promedio} />,
            sub: 'por operación',
          },
        ]}
      />

      {r.proveedores.length === 0 ? (
        <Card>
          <p className="text-sm text-text-secondary">
            No hay gastos registrados en el período.
          </p>
        </Card>
      ) : (
        <>
          <div className="grid md:grid-cols-[1.5fr_1fr] gap-4">
            <Card title="Concentración del gasto">
              <div className="text-xs text-text-secondary -mt-2 mb-3">
                Principales proveedores del período
              </div>
              <div className="space-y-2.5">
                {topN.map((p) => {
                  const color = p.recurrente ? '#5fd9b1' : '#ffb775';
                  return (
                    <div key={p.proveedor}>
                      <div className="flex items-center justify-between gap-3 text-sm mb-1">
                        <span className="truncate">{p.proveedor}</span>
                        <span className="text-text-secondary shrink-0">
                          <Monto value={p.total} /> ·{' '}
                          {formatearTasa(p.pct)}
                        </span>
                      </div>
                      <div className="h-2 rounded-full bg-bg-sunken overflow-hidden">
                        <div
                          style={{
                            width: `${(p.total / (principal?.total || 1)) * 100}%`,
                            background: color,
                          }}
                          className="h-full"
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            </Card>

            <Card title="Recurrentes vs puntuales">
              <div className="text-xs text-text-secondary -mt-2 mb-3">
                Por presencia en el año (≥ 3 meses = recurrente)
              </div>
              <div className="flex h-3 rounded-full overflow-hidden mb-4 bg-bg-sunken">
                {r.recurrentes.monto > 0 && (
                  <div
                    title={`Recurrentes: ${formatCLP(r.recurrentes.monto)}`}
                    style={{
                      width: `${(r.recurrentes.monto / total) * 100}%`,
                      background: '#5fd9b1',
                    }}
                  />
                )}
                {r.puntuales.monto > 0 && (
                  <div
                    title={`Puntuales: ${formatCLP(r.puntuales.monto)}`}
                    style={{
                      width: `${(r.puntuales.monto / total) * 100}%`,
                      background: '#ffb775',
                    }}
                  />
                )}
              </div>
              {[
                {
                  label: 'Recurrentes',
                  desc: 'Relación continua',
                  color: '#5fd9b1',
                  grupo: r.recurrentes,
                },
                {
                  label: 'Puntuales',
                  desc: 'Gasto one-off',
                  color: '#ffb775',
                  grupo: r.puntuales,
                },
              ].map((g) => (
                <div
                  key={g.label}
                  className="flex items-center justify-between gap-3 mb-2.5"
                >
                  <div className="flex items-center gap-2.5 min-w-0">
                    <span
                      className="w-2.5 h-2.5 rounded-sm shrink-0"
                      style={{ background: g.color }}
                    />
                    <div className="min-w-0">
                      <div className="text-sm">{g.label}</div>
                      <div className="text-xs text-text-secondary">{g.desc}</div>
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="text-sm font-medium">
                      <Monto value={g.grupo.monto} />
                    </div>
                    <div className="text-xs text-text-secondary">
                      <Num>{g.grupo.n}</Num> proveedor
                      {g.grupo.n !== 1 ? 'es' : ''}
                    </div>
                  </div>
                </div>
              ))}
            </Card>
          </div>

          <Card title="Detalle por proveedor">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-text-secondary text-left border-b border-white/[0.08]">
                    <th className="py-1 pr-3 font-medium">Proveedor</th>
                    <th className="py-1 pr-3 font-medium">Categoría</th>
                    <th className="py-1 pr-3 font-medium text-center">Operac.</th>
                    <th className="py-1 pr-3 font-medium text-center">Tipo</th>
                    <th className="py-1 pr-3 font-medium text-right">Total</th>
                    <th className="py-1 font-medium text-right">%</th>
                  </tr>
                </thead>
                <tbody>
                  {r.proveedores.map((p) => (
                    <tr
                      key={p.proveedor}
                      className="border-b border-white/[0.04]"
                    >
                      <td className="py-1.5 pr-3 font-medium">{p.proveedor}</td>
                      <td className="py-1.5 pr-3 text-text-secondary">
                        {p.categoria_predominante ?? '—'}
                      </td>
                      <td className="py-1.5 pr-3 text-center">
                        <Num>{p.n_operaciones}</Num>
                        <span className="text-text-secondary">
                          {' '}
                          · {p.meses_activos}m
                        </span>
                      </td>
                      <td className="py-1.5 pr-3 text-center">
                        <span
                          className="text-[10.5px] font-semibold px-2 py-0.5 rounded-full"
                          style={{
                            background: p.recurrente ? '#5fd9b122' : '#ffb77522',
                            color: p.recurrente ? '#5fd9b1' : '#ffb775',
                          }}
                        >
                          {p.recurrente ? 'Recurrente' : 'Puntual'}
                        </span>
                      </td>
                      <td className="py-1.5 pr-3 text-right">
                        <Monto value={p.total} />
                      </td>
                      <td className="py-1.5 text-right text-text-secondary">
                        {formatearTasa(p.pct)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="flex justify-between items-center pt-3 mt-1 border-t border-white/[0.08]">
                <span className="text-sm font-medium text-text-secondary">
                  Total gastado
                </span>
                <span className="text-base font-semibold" style={{ color: '#5fd9b1' }}>
                  <Monto value={r.total_gastado} />
                </span>
              </div>
            </div>
          </Card>
        </>
      )}

      <RepNote>
        <strong className="text-text-primary">Metodología:</strong> agrupa los
        gastos del año por el nombre del proveedor (no anulados). Un proveedor es{' '}
        <strong className="text-text-primary">recurrente</strong> si tuvo gastos
        en 3 o más meses distintos. Como el proveedor es texto libre, variantes
        del mismo nombre cuentan por separado; alinear el texto en la pantalla{' '}
        <strong className="text-text-primary">Gastos</strong> mejora la exactitud
        del reporte. El toggle de compensaciones decide si se incluyen los gastos
        compensados (que no tocaron el banco).
      </RepNote>
    </div>
  );
}

/** Barras horizontales de composición (ingresos por tipo / gastos por categoría). */
function ComposicionBars({ items }: { items: ItemComposicion[] }) {
  const max = items.length ? Math.max(...items.map((i) => i.monto)) : 1;
  return (
    <div className="space-y-2.5">
      {items.map((it, idx) => (
        <div key={idx}>
          <div className="flex items-center justify-between gap-3 text-sm mb-1">
            <span className="truncate">{it.tipo}</span>
            <span className="text-text-secondary shrink-0">
              <Monto value={it.monto} />
            </span>
          </div>
          <div className="h-2 rounded-full bg-bg-sunken overflow-hidden">
            <div
              className="h-full"
              style={{
                width: `${(it.monto / max) * 100}%`,
                background: it.color ?? '#8a8479',
              }}
            />
          </div>
        </div>
      ))}
    </div>
  );
}

/** Tarjeta de indicador operativo del cierre (label + valor + sub, con resalte
 * de alerta opcional). */
function CierreKpiCard({
  label,
  value,
  sub,
  warn,
}: {
  label: string;
  value: ReactNode;
  sub?: ReactNode;
  warn?: boolean;
}) {
  return (
    <div
      className={`rounded-md p-4 border-[0.5px] ${
        warn ? '' : 'bg-bg-card border-white/[0.08]'
      }`}
      style={
        warn
          ? {
              background: 'rgba(255,122,122,0.08)',
              borderColor: 'rgba(255,122,122,0.28)',
            }
          : undefined
      }
    >
      <div className="text-xs text-text-secondary">{label}</div>
      <div className="text-2xl font-medium tracking-tight mt-1">{value}</div>
      {sub != null && (
        <div className="text-xs text-text-secondary mt-1">{sub}</div>
      )}
    </div>
  );
}

/** Notas del cierre: viñetas derivadas de los datos crudos (sin texto editorial). */
function notasCierre(r: ReporteCierreMensualResponse): string[] {
  const notas: string[] = [];
  notas.push(
    r.resultado >= 0
      ? `El mes cerró con un superávit de ${formatCLP(r.resultado)}.`
      : `El mes cerró con un déficit de ${formatCLP(Math.abs(r.resultado))}.`,
  );
  if (r.nuevos_morosos !== null && r.nuevos_morosos !== 0) {
    const n = Math.abs(r.nuevos_morosos);
    notas.push(
      r.nuevos_morosos > 0
        ? `${n} ${n === 1 ? 'casa entró' : 'casas entraron'} en mora respecto del mes anterior.`
        : `${n} ${n === 1 ? 'casa salió' : 'casas salieron'} de mora respecto del mes anterior.`,
    );
  }
  if (r.gastos_por_categoria.length > 0) {
    const top = r.gastos_por_categoria[0];
    notas.push(`El mayor gasto del mes fue ${top.tipo} (${formatCLP(top.monto)}).`);
  }
  if (r.compensaciones_n > 0) {
    notas.push(
      `Se ${r.compensaciones_n === 1 ? 'aplicó' : 'aplicaron'} ${r.compensaciones_n} ${
        r.compensaciones_n === 1 ? 'compensación' : 'compensaciones'
      } por ${formatCLP(r.compensaciones_monto)} (efecto banco cero).`,
    );
  }
  return notas;
}

export function CierreMensualDetalle({
  reporte,
}: {
  reporte: ReporteCierreMensualResponse;
}) {
  const r = reporte;
  const notas = notasCierre(r);
  return (
    <div className="space-y-4">
      <RepBand
        accent="rgba(95,217,177,0.28)"
        items={[
          {
            label: 'Ingresos del mes',
            value: <Monto value={r.ingresos} />,
            valueColor: '#5fd9b1',
            trendPct: r.trend_ingresos ?? undefined,
          },
          {
            label: 'Gastos del mes',
            value: <Monto value={r.gastos} />,
            valueColor: '#ff7a7a',
            trendPct: r.trend_gastos ?? undefined,
            trendBad: true,
          },
          {
            label: 'Resultado',
            value: <MontoFirmado value={r.resultado} />,
            big: true,
            trendPct: r.trend_resultado ?? undefined,
          },
          {
            // NO dice "al cierre": el saldo es un snapshot manual que el
            // tesorero registra cuando revisa la cuenta, así que el último de
            // agosto puede ser del día 27. Rotularlo como cierre de mes afirma
            // algo que no es, y quien lo compare con la cartola encuentra una
            // diferencia que nadie le explicó.
            label: r.saldo_cierre_fecha
              ? `Saldo al ${fechaCorta(r.saldo_cierre_fecha)}`
              : 'Saldo en el banco',
            value: r.saldo_cierre === null ? '—' : <Monto value={r.saldo_cierre} />,
            sub:
              r.saldo_apertura === null ? (
                'sin cuadre previo'
              ) : (
                <>
                  {r.saldo_apertura_fecha
                    ? `al ${fechaCorta(r.saldo_apertura_fecha)}: `
                    : 'anterior '}
                  <Monto value={r.saldo_apertura} />
                </>
              ),
          },
        ]}
      />

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <CierreKpiCard
          label="Casas que pagaron"
          value={<Num>{r.casas_que_pagaron}</Num>}
          sub={`de ${r.total_casas}`}
        />
        <CierreKpiCard
          label="Pagos del mes"
          value={<Num>{r.pagos_del_mes}</Num>}
          sub="clasificados"
        />
        <CierreKpiCard
          label="Nuevos morosos"
          value={
            r.nuevos_morosos === null ? (
              '—'
            ) : (
              <Num>
                {r.nuevos_morosos > 0 ? `+${r.nuevos_morosos}` : r.nuevos_morosos}
              </Num>
            )
          }
          sub="vs mes anterior"
          warn={r.nuevos_morosos !== null && r.nuevos_morosos > 0}
        />
        <CierreKpiCard
          label="Compensaciones"
          value={<Num>{r.compensaciones_n}</Num>}
          sub={
            r.compensaciones_n > 0 ? (
              <>
                <Monto value={r.compensaciones_monto} /> neteados
              </>
            ) : (
              'sin movimiento'
            )
          }
        />
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <Card title="Ingresos del mes">
          {r.ingresos_por_tipo.length === 0 ? (
            <p className="text-sm text-text-secondary">Sin ingresos en el mes.</p>
          ) : (
            <ComposicionBars items={r.ingresos_por_tipo} />
          )}
        </Card>
        <Card title="Gastos del mes">
          {r.gastos_por_categoria.length === 0 ? (
            <p className="text-sm text-text-secondary">Sin gastos en el mes.</p>
          ) : (
            <ComposicionBars items={r.gastos_por_categoria} />
          )}
        </Card>
      </div>

      <Card title="Notas del cierre">
        <ul className="space-y-2">
          {notas.map((n, i) => (
            <li
              key={i}
              className="flex gap-2.5 items-start text-sm text-text-secondary"
            >
              <span
                className="w-1.5 h-1.5 rounded-full shrink-0 mt-1.5"
                style={{ background: '#ff9d5c' }}
              />
              <span>{n}</span>
            </li>
          ))}
        </ul>
      </Card>

      <RepNote>
        <strong className="text-text-primary">Metodología:</strong> consolida los
        ingresos (recaudación + ingresos varios) y los gastos del mes,
        contrastándolos con el mes anterior. Los ingresos varios ligados a un pago
        ya están dentro de la recaudación (no se cuentan dos veces). Sirve de
        respaldo para el cierre semestral/anual y el informe a la comisión.
      </RepNote>
    </div>
  );
}

/** Hitos del ejercicio: viñetas derivadas de los datos del año (sin editorial). */
function hitosAnuales(r: ReporteResumenAnualResponse): string[] {
  const h: string[] = [];
  h.push(
    r.resultado >= 0
      ? `El ejercicio cerró con un superávit de ${formatCLP(r.resultado)}.`
      : `El ejercicio cerró con un déficit de ${formatCLP(Math.abs(r.resultado))}.`,
  );
  h.push(
    `Ingresos del año: ${formatCLP(r.ingresos)}; gastos: ${formatCLP(r.gastos)}.`,
  );
  if (r.gastos_por_categoria.length > 0) {
    const top = r.gastos_por_categoria[0];
    const pct = r.gastos > 0 ? Math.round((top.monto / r.gastos) * 100) : 0;
    h.push(
      `La categoría de mayor gasto fue ${top.tipo} (${formatCLP(top.monto)}, ${pct}% del gasto).`,
    );
  }
  if (r.saldo_cierre !== null) {
    h.push(`Saldo al cierre del ejercicio: ${formatCLP(r.saldo_cierre)}.`);
  }
  return h;
}

export function ResumenAnualDetalle({
  reporte,
}: {
  reporte: ReporteResumenAnualResponse;
}) {
  const r = reporte;
  const hitos = hitosAnuales(r);
  return (
    <div className="space-y-4">
      {r.es_anio_en_curso && (
        <div className="bg-bg-card border-[0.5px] border-orange/30 text-text-secondary rounded-md p-3 text-sm">
          El ejercicio aún está en curso: las cifras son acumuladas del año a hoy
          (YTD), no del año completo.
        </div>
      )}

      <RepBand
        accent="rgba(255,183,117,0.28)"
        items={[
          {
            label: 'Ingresos del año',
            value: <Monto value={r.ingresos} />,
            valueColor: '#5fd9b1',
          },
          {
            label: 'Gastos del año',
            value: <Monto value={r.gastos} />,
            valueColor: '#ff7a7a',
          },
          {
            label: 'Resultado',
            value: <MontoFirmado value={r.resultado} />,
            big: true,
          },
          {
            label: 'Saldo al cierre',
            value:
              r.saldo_cierre === null ? '—' : <Monto value={r.saldo_cierre} />,
          },
        ]}
      />

      <div className="grid md:grid-cols-2 gap-4">
        <Card title="Composición de ingresos">
          {r.ingresos_por_tipo.length === 0 ? (
            <p className="text-sm text-text-secondary">
              Sin ingresos en el ejercicio.
            </p>
          ) : (
            <ComposicionBars items={r.ingresos_por_tipo} />
          )}
        </Card>
        <Card title="Composición de gastos">
          {r.gastos_por_categoria.length === 0 ? (
            <p className="text-sm text-text-secondary">
              Sin gastos en el ejercicio.
            </p>
          ) : (
            <ComposicionBars items={r.gastos_por_categoria} />
          )}
        </Card>
      </div>

      <Card title="Hitos del ejercicio">
        <ul className="space-y-2">
          {hitos.map((n, i) => (
            <li
              key={i}
              className="flex gap-2.5 items-start text-sm text-text-secondary"
            >
              <span
                className="w-1.5 h-1.5 rounded-full shrink-0 mt-1.5"
                style={{ background: '#ff9d5c' }}
              />
              <span>{n}</span>
            </li>
          ))}
        </ul>
      </Card>

      <RepNote>
        <strong className="text-text-primary">Metodología:</strong> documento de
        síntesis del ejercicio para la asamblea. Ingresos = recaudación + ingresos
        varios; gastos por categoría, ambos netos de fondos de terceros. Debe
        acompañarse de los estados financieros detallados y el informe de la
        comisión revisora.
      </RepNote>
    </div>
  );
}

// ───────── Cierre semestral (Fase 5 del plan de morosidad) ─────────

/** Qué semestre toca cerrar: aviso de cuándo se puede o el botón para
 * generarlo, con confirmación porque el cierre no se puede deshacer. */
function ProximoCierre({
  estado,
  puedeGenerar,
  onGenerar,
  onGenerado,
}: {
  estado: EstadoCierres;
  /** Solo el tesorero genera; la Comisión ve los cierres (convención #14). */
  puedeGenerar: boolean;
  onGenerar: (periodo: string) => Promise<unknown>;
  onGenerado: () => void;
}) {
  const [confirmando, setConfirmando] = useState(false);
  const [generando, setGenerando] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (estado.periodo === null) {
    return (
      <Card title="Próximo cierre">
        <p className="text-sm text-text-secondary">
          Todavía no hay cuotas: no hay semestre que cerrar.
        </p>
      </Card>
    );
  }

  if (!estado.disponible) {
    return (
      <Card title="Próximo cierre">
        <p className="text-sm text-text-secondary">
          El cierre del {estado.etiqueta} se puede generar desde el{' '}
          <strong className="text-text-primary">
            {isoAFechaDisplay(estado.disponible_desde)}
          </strong>
          , cuando el semestre haya terminado.
        </p>
      </Card>
    );
  }

  const generar = async () => {
    setGenerando(true);
    setError(null);
    try {
      await onGenerar(estado.periodo!);
      setConfirmando(false);
      onGenerado();
    } catch (e) {
      setError(
        e instanceof ApiError &&
          typeof e.body === 'object' &&
          e.body !== null &&
          'detail' in e.body
          ? String((e.body as { detail: unknown }).detail)
          : 'No se pudo generar el cierre.',
      );
    } finally {
      setGenerando(false);
    }
  };

  return (
    <Card title="Próximo cierre">
      <p className="text-sm text-text-secondary">
        El {estado.etiqueta} ya terminó y se puede cerrar.
        {!puedeGenerar && ' Lo genera el tesorero.'}
      </p>
      {!puedeGenerar ? null : confirmando ? (
        <div className="mt-3 rounded-md p-3 border-[0.5px] border-orange/30 bg-bg-sunken">
          <p className="text-sm">
            El cierre queda <strong>congelado</strong>: no se puede modificar ni
            borrar. Lo que se registre después con fecha de este semestre
            aparecerá como ajuste en el cierre siguiente.
          </p>
          <div className="flex flex-wrap gap-2 mt-3">
            <Button variant="primary" onClick={() => void generar()} disabled={generando}>
              {generando ? 'Generando…' : `Confirmar cierre del ${estado.etiqueta}`}
            </Button>
            <Button
              variant="ghost"
              onClick={() => setConfirmando(false)}
              disabled={generando}
            >
              Cancelar
            </Button>
          </div>
        </div>
      ) : (
        <div className="mt-3">
          <Button variant="primary" onClick={() => setConfirmando(true)}>
            Generar cierre del {estado.etiqueta}
          </Button>
        </div>
      )}
      {error && <p className="text-sm text-[#ff7a7a] mt-2">{error}</p>}
    </Card>
  );
}

/** Banda de saldos del cierre: los saldos son los calculados al cierre del
 * día (todo lo del último día del periodo). El último registro manual del
 * banco va abajo como control, con su fecha real y la diferencia. */
function itemSaldo(s: {
  saldo_final: number | null;
  saldo_final_fecha: string | null;
  saldo_inicial: number | null;
  saldo_inicial_fecha: string | null;
  saldo_final_banco: number | null;
  saldo_final_banco_fecha: string | null;
}): KpiItem {
  const diferencia =
    s.saldo_final !== null && s.saldo_final_banco !== null
      ? s.saldo_final_banco - s.saldo_final
      : null;
  return {
    label: s.saldo_final_fecha ? `Saldo al ${fechaCorta(s.saldo_final_fecha)}` : 'Saldo en el banco',
    value: s.saldo_final === null ? '—' : <Monto value={s.saldo_final} />,
    sub: (
      <>
        <span className="block">
          {s.saldo_inicial === null ? (
            'sin saldo inicial'
          ) : (
            <>
              {s.saldo_inicial_fecha ? `al ${isoAFechaDisplay(s.saldo_inicial_fecha)}: ` : 'inicial '}
              <Monto value={s.saldo_inicial} />
            </>
          )}
        </span>
        {s.saldo_final_banco !== null && s.saldo_final_banco_fecha && (
          <span className="block">
            banco al {isoAFechaDisplay(s.saldo_final_banco_fecha)}: <Monto value={s.saldo_final_banco} />
            {diferencia !== null && diferencia !== 0 && (
              <>
                {' '}(dif. <MontoFirmado value={diferencia} />)
              </>
            )}
          </span>
        )}
      </>
    ),
  };
}

function ComposicionDoble({
  ingresos,
  gastos,
  sufijo,
}: {
  ingresos: ItemComposicion[];
  gastos: ItemComposicion[];
  sufijo: string;
}) {
  return (
    <div className="grid md:grid-cols-2 gap-4">
      <Card title={`Composición de ingresos ${sufijo}`}>
        {ingresos.length === 0 ? (
          <p className="text-sm text-text-secondary">Sin ingresos.</p>
        ) : (
          <ComposicionBars items={ingresos} />
        )}
      </Card>
      <Card title={`Composición de gastos ${sufijo}`}>
        {gastos.length === 0 ? (
          <p className="text-sm text-text-secondary">Sin gastos.</p>
        ) : (
          <ComposicionBars items={gastos} />
        )}
      </Card>
    </div>
  );
}

export function CierreCongelado({ cierre }: { cierre: CierreSemestralDetalle }) {
  const c = cierre;
  const anual = c.resumen_anual;
  return (
    <div className="space-y-4">
      <p className="text-sm text-text-secondary">
        Cierre del <strong className="text-text-primary">{c.etiqueta}</strong>{' '}
        · corte al {isoAFechaDisplay(c.fecha_corte)} · generado el{' '}
        {formatearFecha(c.cerrado_at)}
      </p>

      <RepBand
        accent="rgba(255,183,117,0.28)"
        items={[
          {
            label: 'Ingresos del semestre',
            value: <Monto value={c.ingresos} />,
            valueColor: '#5fd9b1',
          },
          {
            label: 'Gastos del semestre',
            value: <Monto value={c.gastos} />,
            valueColor: '#ff7a7a',
          },
          { label: 'Resultado', value: <MontoFirmado value={c.resultado} />, big: true },
          itemSaldo(c),
        ]}
      />

      <div className="grid grid-cols-2 gap-3">
        <CierreKpiCard
          label="Casas con deuda"
          value={<Num>{c.total_lotes_morosos}</Num>}
          warn={c.total_lotes_morosos > 0}
        />
        <CierreKpiCard
          label="Deuda al corte"
          value={<Monto value={c.total_monto_moroso} />}
          sub="saldo completo de cada cuota"
        />
      </div>

      <ComposicionDoble
        ingresos={c.ingresos_por_tipo}
        gastos={c.gastos_por_categoria}
        sufijo="del semestre"
      />

      {c.ajustes.length > 0 && (
        <Card title="Ajustes de semestres ya cerrados">
          <p className="text-sm text-text-secondary mb-3">
            Movimientos con fecha de un semestre anterior registrados después de
            su cierre. No se suman a este semestre; el cierre anterior no cambia.
          </p>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-text-secondary text-xs">
                <tr>
                  <th className="text-left font-normal py-1.5">Semestre</th>
                  <th className="text-right font-normal py-1.5">Ingresos</th>
                  <th className="text-right font-normal py-1.5">Gastos</th>
                </tr>
              </thead>
              <tbody>
                {c.ajustes.map((a) => (
                  <tr key={a.periodo} className="border-t border-white/[0.06]">
                    <td className="py-1.5">{a.etiqueta}</td>
                    <td className="py-1.5 text-right">
                      <MontoFirmado value={a.ingresos} neutro />
                    </td>
                    <td className="py-1.5 text-right">
                      <MontoFirmado value={a.gastos} neutro />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {anual && (
        <Card title={`Año ${anual.anio}`}>
          <div className="space-y-4">
            <RepBand
              accent="rgba(95,217,177,0.28)"
              items={[
                {
                  label: 'Ingresos del año',
                  value: <Monto value={anual.ingresos} />,
                  valueColor: '#5fd9b1',
                },
                {
                  label: 'Gastos del año',
                  value: <Monto value={anual.gastos} />,
                  valueColor: '#ff7a7a',
                },
                {
                  label: 'Resultado',
                  value: <MontoFirmado value={anual.ingresos - anual.gastos} />,
                  big: true,
                },
                itemSaldo(anual),
              ]}
            />
            <ComposicionDoble
              ingresos={anual.ingresos_por_tipo}
              gastos={anual.gastos_por_categoria}
              sufijo="del año"
            />
          </div>
        </Card>
      )}

      <Card title="Casas con deuda al corte">
        {c.casas.length === 0 ? (
          <p className="text-sm text-text-secondary">
            Ninguna casa tenía deuda al {isoAFechaDisplay(c.fecha_corte)}.
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-text-secondary text-xs">
                <tr>
                  <th className="text-left font-normal py-1.5">Casa</th>
                  <th className="text-left font-normal py-1.5">Detalle</th>
                  <th className="text-right font-normal py-1.5">Deuda</th>
                </tr>
              </thead>
              <tbody>
                {c.casas.map((casa) => (
                  <tr key={casa.lote_id} className="border-t border-white/[0.06] align-top">
                    <td className="py-1.5 pr-3">
                      <Num>{casa.lote_identificador}</Num>
                    </td>
                    <td className="py-1.5 pr-3 text-text-secondary">
                      {casa.cuotas_afectadas.map((q) => (
                        <div key={q.cuota_id}>
                          {q.etiqueta}: <Monto value={q.monto_moroso} />
                        </div>
                      ))}
                    </td>
                    <td className="py-1.5 text-right">
                      <Monto value={casa.monto_moroso_total} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <RepNote>
        <strong className="text-text-primary">Metodología:</strong> el cierre es
        una foto congelada del semestre. Ingresos (recaudación + ingresos varios)
        y gastos, netos de fondos de terceros, cuentan por la fecha del
        movimiento: un pago atrasado hecho después del corte es ingreso del
        semestre siguiente. La deuda de cada casa es el saldo completo de sus
        cuotas al corte, contando solo los pagos hasta esa fecha.
      </RepNote>
    </div>
  );
}

export function CierreSemestralDetalleView({
  datos,
  puedeGenerar,
  cargarDetalle,
  onGenerar,
  onGenerado,
}: {
  datos: CierresSemestralesResponse;
  puedeGenerar: boolean;
  cargarDetalle: (id: string) => Promise<CierreSemestralDetalle>;
  onGenerar: (periodo: string) => Promise<unknown>;
  onGenerado: () => void;
}) {
  const { estado, cierres } = datos;
  const masReciente = cierres[0]?.id ?? null;
  const [selId, setSelId] = useState<string | null>(masReciente);
  const [detalle, setDetalle] = useState<CierreSemestralDetalle | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Al generar uno nuevo, el listado se recarga y se muestra el recién creado.
  useEffect(() => setSelId(masReciente), [masReciente]);

  useEffect(() => {
    if (!selId) {
      setDetalle(null);
      return;
    }
    let vigente = true;
    setError(null);
    cargarDetalle(selId)
      .then((d) => vigente && setDetalle(d))
      .catch(() => vigente && setError('No se pudo cargar el cierre.'));
    return () => {
      vigente = false;
    };
  }, [selId, cargarDetalle]);

  return (
    <div className="space-y-4">
      <ProximoCierre
        estado={estado}
        puedeGenerar={puedeGenerar}
        onGenerar={onGenerar}
        onGenerado={onGenerado}
      />

      {cierres.length === 0 ? (
        <p className="text-sm text-text-secondary">Aún no hay cierres generados.</p>
      ) : (
        <>
          {cierres.length > 1 && (
            <div className="flex flex-wrap gap-2" role="tablist">
              {cierres.map((c) => (
                <button
                  key={c.id}
                  type="button"
                  role="tab"
                  aria-selected={c.id === selId}
                  onClick={() => setSelId(c.id)}
                  className={`px-3 py-1.5 rounded-full text-sm border-[0.5px] ${
                    c.id === selId
                      ? 'border-orange/60 text-text-primary bg-bg-card'
                      : 'border-white/[0.12] text-text-secondary'
                  }`}
                >
                  {c.etiqueta}
                </button>
              ))}
            </div>
          )}
          {error && <p className="text-sm text-[#ff7a7a]">{error}</p>}
          {detalle && detalle.id === selId && <CierreCongelado cierre={detalle} />}
        </>
      )}
    </div>
  );
}

/** Evolución de un saldo mensual (área). Genérico: lo usan Flujo de caja y
 * Fondo de reserva con distinto color (`gradId` debe ser único por gráfico). */
function SaldoAreaChart({
  meses,
  color,
  gradId,
}: {
  meses: { mes: number; saldo: number }[];
  color: string;
  gradId: string;
}) {
  const rows = meses.map((m) => ({ mes: MESES_MIN[m.mes - 1], saldo: m.saldo }));
  return (
    <div className="w-full" style={{ height: 180 }}>
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart data={rows} margin={{ top: 8, right: 8, left: 8, bottom: 0 }}>
          <defs>
            <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={color} stopOpacity={0.3} />
              <stop offset="100%" stopColor={color} stopOpacity={0} />
            </linearGradient>
          </defs>
          <XAxis
            dataKey="mes"
            tick={{ fill: '#8a8479', fontSize: 10 }}
            axisLine={false}
            tickLine={false}
          />
          <Tooltip
            contentStyle={{
              backgroundColor: '#1a1a1d',
              border: '1px solid #26262a',
              borderRadius: 6,
              fontSize: 12,
              color: '#f4ede0',
            }}
            formatter={(v) => formatCLP(Number(v))}
          />
          <Area
            type="monotone"
            dataKey="saldo"
            stroke={color}
            strokeWidth={2}
            fill={`url(#${gradId})`}
            dot={{ r: 2, fill: color, strokeWidth: 0 }}
            activeDot={{ r: 4 }}
          />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
}

export function FlujoCajaDetalle({
  reporte,
}: {
  reporte: ReporteFlujoCajaResponse;
}) {
  const r = reporte;
  return (
    <div className="space-y-4">
      {!r.saldo_inicial_disponible && (
        <div className="bg-bg-card border-[0.5px] border-orange/30 text-text-secondary rounded-md p-3 text-sm">
          No hay un cuadre bancario del año anterior: el flujo parte de un saldo
          inicial de $0. Registra el saldo en <strong className="text-text-primary">Banco</strong> para
          anclar la evolución al saldo real.
        </div>
      )}

      <RepBand
        accent="rgba(111,211,224,0.28)"
        items={[
          {
            label: 'Saldo inicial',
            value: <Monto value={r.saldo_inicial} />,
            sub: 'cierre año anterior',
          },
          {
            label: 'Saldo final',
            value: <Monto value={r.saldo_final} />,
            big: true,
            valueColor: '#6fd3e0',
          },
          {
            label: 'Saldo mínimo',
            value: <Monto value={r.saldo_minimo} />,
            sub: 'punto más bajo',
          },
          {
            label: 'Saldo máximo',
            value: <Monto value={r.saldo_maximo} />,
          },
        ]}
      />

      <Card title="Evolución del saldo de caja">
        <div className="text-xs text-text-secondary -mt-2 mb-2">
          Acumulado mes a mes
        </div>
        {r.meses.length === 0 ? (
          <p className="text-sm text-text-secondary">Sin movimientos en el período.</p>
        ) : (
          <SaldoAreaChart meses={r.meses} color="#6fd3e0" gradId="gradFlujo" />
        )}
      </Card>

      <Card title="Detalle mensual">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-text-secondary text-left border-b border-white/[0.08]">
                <th className="py-1 pr-3 font-medium">Mes</th>
                <th className="py-1 pr-3 font-medium text-right">Entradas</th>
                <th className="py-1 pr-3 font-medium text-right">Salidas</th>
                <th className="py-1 pr-3 font-medium text-right">Flujo neto</th>
                <th className="py-1 font-medium text-right">Saldo caja</th>
              </tr>
            </thead>
            <tbody>
              {r.meses.map((m) => (
                <tr key={m.mes} className="border-b border-white/[0.04]">
                  <td className="py-1.5 pr-3 font-medium">{MESES_ABREV[m.mes - 1]}</td>
                  <td className="py-1.5 pr-3 text-right" style={{ color: '#5fd9b1' }}>
                    <Monto value={m.entradas} />
                  </td>
                  <td className="py-1.5 pr-3 text-right" style={{ color: '#ff7a7a' }}>
                    <Monto value={m.salidas} />
                  </td>
                  <td className="py-1.5 pr-3 text-right">
                    <MontoFirmado value={m.neto} />
                  </td>
                  <td className="py-1.5 text-right font-medium">
                    <Monto value={m.saldo} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      <RepNote>
        <strong className="text-text-primary">Metodología:</strong> el flujo de
        caja refleja entradas y salidas efectivas de dinero (base caja), distinto
        del resultado contable; excluye compensaciones (no tocan el banco). El
        saldo de caja es derivado (saldo inicial + flujo neto acumulado) y{' '}
        <strong className="text-text-primary">no</strong> incluye traspasos a
        depósitos a plazo ni reconcilia con el banco real — para eso está la
        Conciliación bancaria. El saldo mínimo marca la mayor exigencia de liquidez.
      </RepNote>
    </div>
  );
}

export function FondoReservaDetalle({
  reporte,
}: {
  reporte: ReporteFondoReservaResponse;
}) {
  const r = reporte;
  const pctMetaColor =
    r.pct_meta === null ? undefined : r.pct_meta >= 0.8 ? '#5fd9b1' : '#ffb775';
  const pctWidth = r.pct_meta === null ? 0 : Math.min(100, r.pct_meta * 100);
  return (
    <div className="space-y-4">
      <RepBand
        accent="rgba(184,155,255,0.28)"
        items={[
          {
            label: 'Saldo del fondo',
            value: <Monto value={r.saldo_fondo} />,
            big: true,
            valueColor: '#b89bff',
          },
          {
            label: 'Meta',
            value: <Monto value={r.meta} />,
            sub: '≈6 meses de gastos',
          },
          {
            label: '% de la meta',
            value: <Num>{formatearTasa(r.pct_meta)}</Num>,
            valueColor: pctMetaColor,
          },
          {
            label: 'Cobertura',
            value:
              r.meses_cobertura === null ? (
                '—'
              ) : (
                <Num>{`${r.meses_cobertura} meses`}</Num>
              ),
            sub: 'de gastos operacionales',
          },
        ]}
      />

      <Card title="Avance hacia la meta">
        <div className="flex justify-between text-xs text-text-secondary mb-2">
          <span>
            <Monto value={r.saldo_fondo} />
          </span>
          <span>
            Meta <Monto value={r.meta} />
          </span>
        </div>
        <div className="h-3 rounded-full bg-bg-sunken overflow-hidden mb-4">
          <div
            className="h-full rounded-full"
            style={{
              width: `${pctWidth}%`,
              background: 'linear-gradient(90deg, #b89bff, #7ab8ff)',
            }}
          />
        </div>
        {r.meses.length === 0 ? (
          <p className="text-sm text-text-secondary">Sin datos del período.</p>
        ) : (
          <SaldoAreaChart meses={r.meses} color="#b89bff" gradId="gradFondo" />
        )}
      </Card>

      <Card title="Evolución mensual">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-text-secondary text-left border-b border-white/[0.08]">
                <th className="py-1 pr-3 font-medium">Mes</th>
                <th className="py-1 pr-3 font-medium text-right">Saldo fondo</th>
                <th className="py-1 font-medium text-right">% meta</th>
              </tr>
            </thead>
            <tbody>
              {r.meses.map((m) => (
                <tr key={m.mes} className="border-b border-white/[0.04]">
                  <td className="py-1.5 pr-3 font-medium">
                    {MESES_ABREV[m.mes - 1]}
                  </td>
                  <td className="py-1.5 pr-3 text-right">
                    <Monto value={m.saldo} />
                  </td>
                  <td className="py-1.5 text-right text-text-secondary">
                    <Num>{formatearTasa(m.pct_meta)}</Num>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      <RepNote>
        <strong className="text-text-primary">Metodología:</strong> el fondo de
        reserva del barrio se materializa en los depósitos a plazo (DAP), donde se
        inmoviliza capital fuera de la operación diaria. La meta equivale a ≈6
        meses de gastos operacionales del año (base caja, sin compensaciones),
        para cubrir imprevistos sin recurrir a cuotas extraordinarias. La cobertura
        indica cuántos meses de operación cubre el fondo.
      </RepNote>
    </div>
  );
}

export function ProyeccionDetalle({
  reporte,
}: {
  reporte: ReporteProyeccionResponse;
}) {
  const r = reporte;
  return (
    <div className="space-y-4">
      {!r.saldo_actual_disponible && (
        <div className="bg-bg-card border-[0.5px] border-orange/30 text-text-secondary rounded-md p-3 text-sm">
          No hay un cuadre bancario registrado: la proyección parte de un saldo
          de $0. Registra el saldo en <strong className="text-text-primary">Banco</strong> para
          anclarla al saldo real.
        </div>
      )}

      <RepBand
        accent="rgba(111,211,224,0.28)"
        items={[
          { label: 'Saldo actual', value: <Monto value={r.saldo_actual} /> },
          {
            label: 'Ingreso proyectado',
            value: <Monto value={r.ingreso_proyectado_total} />,
            valueColor: '#5fd9b1',
            sub: '12 meses',
          },
          {
            label: 'Gasto proyectado',
            value: <Monto value={r.gasto_proyectado_total} />,
            valueColor: '#ff7a7a',
            sub: '12 meses',
          },
          {
            label: 'Saldo si todos pagan',
            value: <Monto value={r.saldo_proyectado} />,
            big: true,
            valueColor: '#6fd3e0',
            sub: 'en 12 meses',
          },
          {
            label: 'Saldo al ritmo actual',
            value: <Monto value={r.saldo_proyectado_al_ritmo_actual} />,
            big: true,
            valueColor: '#ffb775',
            sub:
              r.tasa_cobranza != null
                ? `cobrando el ${Math.round(r.tasa_cobranza * 100)}%`
                : 'sin cuotas vencidas aún',
          },
        ]}
      />

      {/* De dónde sale cada número. Sin esto la proyección son cifras sin
          procedencia, y la anterior lo era: promediaba 12 meses con 4 en cero
          del lado del gasto y con la carga inicial del go-live del lado del
          ingreso. */}
      <Card title="Cómo se calcula">
        <ul className="text-xs text-text-secondary space-y-1.5 list-disc pl-4">
          <li>
            <strong className="text-text-primary">Ingreso si todos pagan:</strong>{' '}
            la emisión de cuotas del año —{' '}
            <Monto value={r.emision_anual} /> — dividida en 12 ={' '}
            <Monto value={r.ingreso_mensual_promedio} /> al mes. No es un
            promedio: es lo que el barrio emite.
          </li>
          <li>
            <strong className="text-text-primary">Ingreso al ritmo actual:</strong>{' '}
            lo mismo, por la tasa de cobranza de las cuotas <em>ya vencidas</em>
            {r.tasa_cobranza != null ? (
              <>
                {' '}(<Monto value={r.tasa_cobranza_cobrado} /> de{' '}
                <Monto value={r.tasa_cobranza_emitido} /> en{' '}
                {r.tasa_cobranza_periodos} período
                {r.tasa_cobranza_periodos === 1 ? '' : 's'}) ={' '}
                <Monto value={r.ingreso_mensual_al_ritmo_actual} /> al mes.
              </>
            ) : (
              <>. Todavía no hay cuotas vencidas, así que las dos líneas coinciden.</>
            )}{' '}
            El semestre en curso no entra: su tasa es parcial y se leería como
            morosidad cuando la gente aún está en plazo.
          </li>
          <li>
            <strong className="text-text-primary">Gasto:</strong> promedio de
            los últimos {r.gasto_meses_ventana} meses ={' '}
            <Monto value={r.gasto_mensual_promedio} /> al mes
            {r.gasto_usa_historico
              ? ', completando con el histórico los meses previos a que el sistema operara'
              : ''}
            .
          </li>
        </ul>
      </Card>

      <Card title="Saldo de caja proyectado">
        <div className="text-xs text-text-secondary -mt-2 mb-2">
          Acumulado mes a mes (próximos 12 meses)
        </div>
        <SaldoAreaChart meses={r.meses} color="#6fd3e0" gradId="gradProy" />
      </Card>

      <Card title="Detalle proyectado">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-text-secondary text-left border-b border-white/[0.08]">
                <th className="py-1 pr-3 font-medium">Mes</th>
                <th className="py-1 pr-3 font-medium text-right">Ingreso</th>
                <th className="py-1 pr-3 font-medium text-right">Gasto</th>
                <th className="py-1 pr-3 font-medium text-right">Saldo si todos pagan</th>
                <th className="py-1 font-medium text-right">Saldo al ritmo actual</th>
              </tr>
            </thead>
            <tbody>
              {r.meses.map((m) => (
                <tr key={`${m.anio}-${m.mes}`} className="border-b border-white/[0.04]">
                  <td className="py-1.5 pr-3 font-medium">
                    {MESES_ABREV[m.mes - 1]} {String(m.anio).slice(2)}
                  </td>
                  <td className="py-1.5 pr-3 text-right" style={{ color: '#5fd9b1' }}>
                    <Monto value={m.ingreso} />
                  </td>
                  <td className="py-1.5 pr-3 text-right" style={{ color: '#ff7a7a' }}>
                    <Monto value={m.gasto} />
                  </td>
                  <td
                    className="py-1.5 pr-3 text-right font-medium"
                    style={{ color: '#6fd3e0' }}
                  >
                    <Monto value={m.saldo} />
                  </td>
                  <td
                    className="py-1.5 text-right font-medium"
                    style={{ color: '#ffb775' }}
                  >
                    <Monto value={m.saldo_al_ritmo_actual} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      <RepNote>
        <strong className="text-text-primary">Metodología:</strong> el ingreso
        sale de la <strong className="text-text-primary">emisión de cuotas</strong>,
        no de un promedio de recaudación: las cuotas son semestrales —la plata
        entra en dos oleadas al año— y la recaudación real del sistema arranca
        en junio de 2026. La segunda línea aplica la tasa de cobranza de las
        cuotas ya vencidas;{' '}
        <strong className="text-text-primary">
          la brecha entre las dos es la morosidad
        </strong>
        , y es el dato sobre el que se puede actuar. El gasto es el promedio
        móvil de 12 meses, completando con el histórico los meses previos a que
        el sistema operara. Proyección plana: no considera estacionalidad,
        cuotas extraordinarias ni imprevistos.
      </RepNote>
    </div>
  );
}

type DetalleData =
  | ReporteMorosidadResponse
  | ReporteRecaudacionPorCasaResponse
  | ComparativaAnualResponse
  | GastosPorCategoriaResponse
  | CompensacionConDetalle[]
  | ReporteConciliacionResponse
  | ReporteProveedoresResponse
  | ReporteCierreMensualResponse
  | ReporteResumenAnualResponse
  | ReporteFlujoCajaResponse
  | ReporteFondoReservaResponse
  | ReporteProyeccionResponse
  | InformeMensualVecinosResponse
  | RecordatorioSemestralResponse
  | CierresSemestralesResponse;

function DetalleReporte({
  id,
  onVolver,
}: {
  id: ReporteId;
  onVolver: () => void;
}) {
  const anioActual = Number(hoy().slice(0, 4));
  const aniosDisponibles = [0, 1, 2, 3, 4].map((d) => anioActual - d);
  const [anioSel, setAnioSel] = useState<number>(anioActual);
  const [incluirComp, setIncluirComp] = useState(true);
  const [data, setData] = useState<DetalleData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const meta = REPORTES.find((r) => r.id === id)!;
  // /admin/reportes reutiliza esta página: la Comisión ve, el tesorero opera.
  const esTesorero = useAuth().me?.roles_vigentes.includes('tesorero') ?? false;

  const recargar = useCallback(
    async (anio: number, incluir: boolean) => {
      setLoading(true);
      setError(null);
      try {
        if (id === 'morosidad') setData(await getReporteMorosidad());
        else if (id === 'ingresos-gastos')
          setData(await getComparativaAnual(anio, incluir));
        else if (id === 'gastos-categoria')
          setData(await getGastosPorCategoria({ anio, incluirCompensaciones: incluir }));
        else if (id === 'compensaciones')
          setData(await listCompensacionesConDetalle({}));
        else if (id === 'recaudacion')
          setData(await getReporteRecaudacionPorCasa(anio));
        else if (id === 'proveedores')
          setData(await getReporteProveedores(anio, incluir));
        else if (id === 'cierre-mensual')
          setData(await getReporteCierreMensual(incluir));
        else if (id === 'informe-vecinos')
          setData(await getInformeMensualVecinos());
        else if (id === 'recordatorio-semestral')
          setData(await getRecordatorioSemestral());
        else if (id === 'resumen-anual')
          setData(await getReporteResumenAnual(anio, incluir));
        else if (id === 'cierre-semestral')
          setData(await getCierresSemestrales());
        else if (id === 'flujo-caja')
          setData(await getReporteFlujoCaja(anio));
        else if (id === 'fondo-reserva')
          setData(await getReporteFondoReserva(anio));
        else if (id === 'proyeccion')
          setData(await getReporteProyeccion());
        else setData(await getReporteConciliacion());
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Error al cargar el reporte');
      } finally {
        setLoading(false);
      }
    },
    [id],
  );

  useEffect(() => {
    void recargar(anioSel, incluirComp);
  }, [recargar, anioSel, incluirComp]);

  return (
    <DetalleLayout
      titulo={meta.titulo}
      bajada={meta.descripcion}
      usaAnio={meta.usaAnio}
      anioSel={anioSel}
      aniosDisponibles={aniosDisponibles}
      onAnioChange={setAnioSel}
      incluirComp={meta.usaToggle ? incluirComp : null}
      onToggleComp={setIncluirComp}
      onVolver={onVolver}
      onExcel={
        meta.descargar
          ? () => void meta.descargar!(anioSel, incluirComp)
          : undefined
      }
      loading={loading}
      error={error}
    >
      {data && id === 'morosidad' && (
        <MorosidadDetalle reporte={data as ReporteMorosidadResponse} />
      )}
      {data && id === 'ingresos-gastos' && (
        <IngresosGastosDetalle reporte={data as ComparativaAnualResponse} />
      )}
      {data && id === 'gastos-categoria' && (
        <GastosCategoriaDetalle reporte={data as GastosPorCategoriaResponse} />
      )}
      {data && id === 'compensaciones' && (
        <CompensacionesDetalle comps={data as CompensacionConDetalle[]} />
      )}
      {data && id === 'conciliacion' && (
        <ConciliacionDetalle reporte={data as ReporteConciliacionResponse} />
      )}
      {data && id === 'proveedores' && (
        <ProveedoresDetalle reporte={data as ReporteProveedoresResponse} />
      )}
      {data && id === 'cierre-mensual' && (
        <CierreMensualDetalle reporte={data as ReporteCierreMensualResponse} />
      )}
      {data && id === 'informe-vecinos' && (
        <InformeMensualVecinos
          informe={data as InformeMensualVecinosResponse}
          onEnviarPrueba={() => enviarPruebaInformeMensual()}
          onEnviar={() => enviarInformeMensual()}
          onEnviado={() => void recargar(anioSel, incluirComp)}
        />
      )}
      {data && id === 'recordatorio-semestral' && (
        <RecordatorioSemestral
          recordatorio={data as RecordatorioSemestralResponse}
          onEnviarPrueba={() => enviarPruebaRecordatorio()}
          onEnviar={() => enviarRecordatorio()}
          onEnviado={() => void recargar(anioSel, incluirComp)}
        />
      )}
      {data && id === 'resumen-anual' && (
        <ResumenAnualDetalle reporte={data as ReporteResumenAnualResponse} />
      )}
      {data && id === 'cierre-semestral' && (
        <CierreSemestralDetalleView
          datos={data as CierresSemestralesResponse}
          puedeGenerar={esTesorero}
          cargarDetalle={getCierreSemestral}
          onGenerar={generarCierreSemestral}
          onGenerado={() => void recargar(anioSel, incluirComp)}
        />
      )}
      {data && id === 'flujo-caja' && (
        <FlujoCajaDetalle reporte={data as ReporteFlujoCajaResponse} />
      )}
      {data && id === 'fondo-reserva' && (
        <FondoReservaDetalle reporte={data as ReporteFondoReservaResponse} />
      )}
      {data && id === 'proyeccion' && (
        <ProyeccionDetalle reporte={data as ReporteProyeccionResponse} />
      )}
      {data && id === 'recaudacion' && (
        <RecaudacionDetalle
          reporte={data as ReporteRecaudacionPorCasaResponse}
        />
      )}
    </DetalleLayout>
  );
}

// ───────── Página ─────────

export function ReportesPage() {
  const [seleccionado, setSeleccionado] = useState<ReporteId | null>(null);

  if (seleccionado) {
    return (
      <DetalleReporte
        id={seleccionado}
        onVolver={() => setSeleccionado(null)}
      />
    );
  }

  return <CatalogoReportesView onVer={setSeleccionado} />;
}
